import { type BrowserStatus, BrowserStatusSchema } from "@lifter/agent-runtime-contracts/browser";
import { HUB_CHAT_PLATFORM } from "@lifter/agent-runtime-contracts/channel-platforms";
import {
  type ConversationPage,
  ConversationPageSchema,
} from "@lifter/agent-runtime-contracts/conversation";
import { z } from "zod";
import { USER_ROLES, type UserRole } from "../auth/roles.js";
import {
  AgentArchiveDataSchema,
  AgentArchivedListDataSchema,
  AgentChatTargetDataSchema,
  type AgentCreateInput,
  AgentDeleteDataSchema,
  AgentGetDataSchema,
  AgentListDataSchema,
  AgentUnarchiveDataSchema,
  AgentUpdateDataSchema,
  type AgentUpdateInput,
} from "../contracts/agents.js";
import type { ApplicationActionNameSchema, ApplicationIconKey } from "../contracts/applications.js";
import {
  ApprovalFileTextSchema,
  ApprovalParcelDecisionSchema,
  ApprovalParcelListSchema,
  ApprovalParcelViewSchema,
  ApprovalPreferencesSchema,
} from "../contracts/approval-parcels.js";
import { AssetVisibilitySchema } from "../contracts/asset-visibility.js";
import {
  AutomationCancelDataSchema,
  type AutomationConfirmationMode,
  type AutomationDetailData,
  AutomationDetailDataSchema,
  type AutomationListData,
  AutomationListDataSchema,
  type AutomationRunDetailData,
  AutomationRunDetailDataSchema,
  type AutomationRunStartedData,
  AutomationRunStartedDataSchema,
  type AutomationRunsData,
  AutomationRunsDataSchema,
  type AutomationStatus,
  type AutomationSummary,
  AutomationUpdateDataSchema,
} from "../contracts/automations.js";
import {
  CredentialDeleteDataSchema,
  CredentialListDataSchema,
  CredentialSetDataSchema,
  type CredentialSetPayloadSchema,
  CredentialUpdateDataSchema,
  type CredentialUpdatePayloadSchema,
} from "../contracts/credentials.js";
import {
  DashboardComponentKindSchema,
  DashboardComponentSizeSchema,
  DashboardHealthSchema,
} from "../contracts/dashboards.js";
import {
  WorkspaceDeleteDataSchema,
  WorkspaceListDataSchema,
  WorkspaceMkdirDataSchema,
  WorkspaceReadDataSchema,
  WorkspaceWriteDataSchema,
} from "../contracts/files.js";
import { RestartDataSchema } from "../contracts/gateway.js";
import { type HubChatConfirmAck, HubChatConfirmAckSchema } from "../contracts/hubchat.js";
import {
  MemoryDeleteByHandleDataSchema,
  MemoryDeleteTagDataSchema,
  MemoryDeleteTopicDataSchema,
  MemoryListByFilterDataSchema,
  MemoryListTagsDataSchema,
  MemoryListTopicsDataSchema,
  MemoryReadByHandleDataSchema,
  MemoryRenameTagDataSchema,
  MemoryRenameTopicDataSchema,
  MemorySaveTagDataSchema,
  MemorySaveTopicDataSchema,
  MemoryWriteByHandleDataSchema,
} from "../contracts/memory.js";
import { CONNECTOR_OAUTH_COMPLETE_PATH } from "../contracts/oauth-paths.js";
import {
  PermissionListDataSchema,
  PermissionSetDataSchema,
  type PermissionSetPayloadSchema,
} from "../contracts/permissions.js";
import {
  ScheduleActionDataSchema,
  type ScheduleActionPayload,
  ScheduleListDataSchema,
  type SchedulePatch,
  ScheduleRunsDataSchema,
  ScheduleRunTranscriptDataSchema,
  ScheduleUpdateDataSchema,
} from "../contracts/scheduling.js";
import {
  ConversationArchiveDataSchema,
  type ConversationAudienceData,
  ConversationAudienceDataSchema,
  ConversationCreateDataSchema,
  type ConversationKindFilter,
  type ConversationLeaveData,
  ConversationLeaveDataSchema,
  type ConversationParticipantsData,
  ConversationParticipantsDataSchema,
  ConversationResolutionDataSchema,
  type SessionHistoryDataSchema,
  SessionListDataSchema,
} from "../contracts/sessions.js";
import {
  SkillAddDataSchema,
  SkillConfigureDataSchema,
  SkillFilesListDataSchema,
  SkillFilesReadDataSchema,
  SkillFilesWriteDataSchema,
  SkillListDataSchema,
  SkillRemoveDataSchema,
  SkillUploadDataSchema,
} from "../contracts/skills.js";
import {
  type AgentTeam,
  AgentTeamsCreateDataSchema,
  AgentTeamsDeleteDataSchema,
  AgentTeamsListDataSchema,
  AgentTeamsUpdateDataSchema,
} from "../contracts/teams.js";
import { UsageSummaryDataSchema, type UsageSummaryPayload } from "../contracts/usage.js";
import {
  type WorkflowCreateData,
  WorkflowCreateDataSchema,
  type WorkflowCreatePayload,
  type WorkflowDefinitionWire,
  type WorkflowGetData,
  WorkflowGetDataSchema,
  type WorkflowItemGetData,
  WorkflowItemGetDataSchema,
  type WorkflowItemsListData,
  WorkflowItemsListDataSchema,
  type WorkflowListData,
  WorkflowListDataSchema,
  type WorkflowSetStatusData,
  WorkflowSetStatusDataSchema,
  type WorkflowSetStatusPayload,
  type WorkflowUpdateData,
  WorkflowUpdateDataSchema,
} from "../contracts/workflows.js";
import { WorkspaceConversationsDataSchema } from "../contracts/workspace.js";
import {
  type ApplicationActionResult,
  ApplicationActionResultSchema,
  type ApplicationSessionTicket,
  ApplicationSessionTicketSchema,
  type ApplicationsListData,
  ApplicationsListResponseSchema,
  applicationSocketUrl,
  ExternalApplicationSchema,
  type ExternalApplicationView,
  InternalApplicationResponseSchema,
  type InternalApplicationView,
} from "./applications.js";

export type ApplicationActionName = z.infer<typeof ApplicationActionNameSchema>;

export type AgentListData = z.infer<typeof AgentListDataSchema>;
export type UsageSummaryData = z.infer<typeof UsageSummaryDataSchema>;
export type AgentGetData = z.infer<typeof AgentGetDataSchema>;
export type AgentChatTargetData = z.infer<typeof AgentChatTargetDataSchema>;
export type AgentUpdateData = z.infer<typeof AgentUpdateDataSchema>;
export type AgentDeleteData = z.infer<typeof AgentDeleteDataSchema>;
export type AgentArchiveData = z.infer<typeof AgentArchiveDataSchema>;
export type AgentUnarchiveData = z.infer<typeof AgentUnarchiveDataSchema>;
export type AgentArchivedListData = z.infer<typeof AgentArchivedListDataSchema>;
export type CredentialListData = z.infer<typeof CredentialListDataSchema>;
export type CredentialSetPayload = z.infer<typeof CredentialSetPayloadSchema>;
export type CredentialSetData = z.infer<typeof CredentialSetDataSchema>;
export type CredentialUpdatePayload = z.infer<typeof CredentialUpdatePayloadSchema>;
export type CredentialUpdateData = z.infer<typeof CredentialUpdateDataSchema>;
export type CredentialDeleteData = z.infer<typeof CredentialDeleteDataSchema>;
export type PermissionListData = z.infer<typeof PermissionListDataSchema>;
export type PermissionSetPayload = z.infer<typeof PermissionSetPayloadSchema>;
export type PermissionSetData = z.infer<typeof PermissionSetDataSchema>;
export type RestartData = z.infer<typeof RestartDataSchema>;
export type MemoryListByFilterData = z.infer<typeof MemoryListByFilterDataSchema>;
export type MemoryReadByHandleData = z.infer<typeof MemoryReadByHandleDataSchema>;
export type MemoryWriteByHandleData = z.infer<typeof MemoryWriteByHandleDataSchema>;
export type MemoryDeleteByHandleData = z.infer<typeof MemoryDeleteByHandleDataSchema>;
export type MemoryListTopicsData = z.infer<typeof MemoryListTopicsDataSchema>;
export type MemoryListTagsData = z.infer<typeof MemoryListTagsDataSchema>;
export type MemorySaveTopicData = z.infer<typeof MemorySaveTopicDataSchema>;
export type MemoryDeleteTopicData = z.infer<typeof MemoryDeleteTopicDataSchema>;
export type MemorySaveTagData = z.infer<typeof MemorySaveTagDataSchema>;
export type MemoryDeleteTagData = z.infer<typeof MemoryDeleteTagDataSchema>;
export type MemoryRenameTopicData = z.infer<typeof MemoryRenameTopicDataSchema>;
export type MemoryRenameTagData = z.infer<typeof MemoryRenameTagDataSchema>;
export type WorkspaceListData = z.infer<typeof WorkspaceListDataSchema>;
export type WorkspaceReadData = z.infer<typeof WorkspaceReadDataSchema>;
export type WorkspaceWriteData = z.infer<typeof WorkspaceWriteDataSchema>;
export type WorkspaceDeleteData = z.infer<typeof WorkspaceDeleteDataSchema>;
export type WorkspaceMkdirData = z.infer<typeof WorkspaceMkdirDataSchema>;
export type SkillListData = z.infer<typeof SkillListDataSchema>;
export type SkillAddData = z.infer<typeof SkillAddDataSchema>;
export type SkillRemoveData = z.infer<typeof SkillRemoveDataSchema>;
export type SkillConfigureData = z.infer<typeof SkillConfigureDataSchema>;
export type SkillFilesListData = z.infer<typeof SkillFilesListDataSchema>;
export type SkillFilesReadData = z.infer<typeof SkillFilesReadDataSchema>;
export type SkillFilesWriteData = z.infer<typeof SkillFilesWriteDataSchema>;
export type SkillUploadData = z.infer<typeof SkillUploadDataSchema>;
export type ScheduleListData = z.infer<typeof ScheduleListDataSchema>;
export type ScheduleRunsData = z.infer<typeof ScheduleRunsDataSchema>;
export type ScheduleRunTranscriptData = z.infer<typeof ScheduleRunTranscriptDataSchema>;
export type ScheduleUpdateData = z.infer<typeof ScheduleUpdateDataSchema>;
export type ScheduleActionData = z.infer<typeof ScheduleActionDataSchema>;
export type SessionListData = z.infer<typeof SessionListDataSchema>;
export type SessionHistoryData = z.infer<typeof SessionHistoryDataSchema>;
export type WorkspaceConversationsData = z.infer<typeof WorkspaceConversationsDataSchema>;

const SpecialistTemplateSchema = z
  .object({
    id: z.string().min(1),
    version: z.number().int().positive(),
    displayName: z.string().min(1),
    description: z.string(),
    iconHint: z.string().optional(),
    prompt: z.string(),
    defaultToneMode: z.string().min(1),
    defaultVerbosity: z.enum(["concise", "balanced", "verbose"]),
    starterSkills: z.array(z.string()),
    recommendedConnectors: z.array(z.string()),
  })
  .passthrough();
const SpecialistTemplatesDataSchema = z
  .object({ templates: z.array(SpecialistTemplateSchema) })
  .passthrough();

// Specialist-template projection and update flows are instance-internal HTTP
// shapes, so they live here rather than in the vendored contracts modules.
const TemplateDiffInfoSchema = z
  .object({
    templateId: z.string().min(1),
    pinnedVersion: z.number().int().positive(),
    latestVersion: z.number().int().positive(),
    promptChanged: z.boolean(),
    promptIsCustomized: z.boolean(),
    skillsToAdd: z.array(z.string()),
  })
  .passthrough();
const TemplateDiffDataSchema = z.union([z.null(), TemplateDiffInfoSchema]);
const TemplateApplyDataSchema = z
  .object({
    newVersion: z.number().int().positive(),
    promptOverwritten: z.boolean(),
    skillsAdded: z.array(z.string()),
    skillsFailed: z.array(z.string()),
  })
  .passthrough();

export type TemplateDiffInfo = z.infer<typeof TemplateDiffInfoSchema>;
export type TemplateDiffData = z.infer<typeof TemplateDiffDataSchema>;
export type TemplateApplyData = z.infer<typeof TemplateApplyDataSchema>;
export type SpecialistTemplate = z.infer<typeof SpecialistTemplateSchema>;
export type SpecialistTemplatesData = z.infer<typeof SpecialistTemplatesDataSchema>;

// Sandbox network-egress shapes are instance-internal HTTP contracts, so they
// live here rather than in the vendored contracts modules.
const NetworkEgressHostSchema = z
  .object({
    host: z.string(),
    ports: z.array(z.number()).optional(),
    allowPrivate: z.boolean().optional(),
  })
  .passthrough();
const NetworkEgressConfigSchema = z
  .object({
    mode: z.enum(["denied", "allowlist", "public"]),
    hosts: z.array(NetworkEgressHostSchema),
  })
  .passthrough();
const NetworkEgressDenialSchema = z
  .object({ host: z.string(), port: z.number(), reason: z.string(), at: z.string() })
  .passthrough();
const NetworkEgressRefreshFailureSchema = z
  .object({ reason: z.literal("egress_fence_refresh_failed"), at: z.string() })
  .passthrough();
const NetworkEgressGetDataSchema = z
  .object({
    config: NetworkEgressConfigSchema,
    recentDenials: z.array(NetworkEgressDenialSchema),
    recentRefreshFailures: z.array(NetworkEgressRefreshFailureSchema),
  })
  .passthrough();
const NetworkEgressUpdateDataSchema = z.object({ config: NetworkEgressConfigSchema }).passthrough();

const AgentEmailSettingsSchema = z
  .object({
    available: z.boolean(),
    unavailableReason: z.string().optional(),
    enabled: z.boolean(),
    address: z.string().nullable(),
    allowedContacts: z.array(z.string()),
    replyTo: z.string().nullable(),
    domain: z.string().nullable(),
  })
  .passthrough();
const AgentEmailQuotaSchema = z
  .object({
    dailyUsed: z.number(),
    dailyLimit: z.number(),
    monthlyUsed: z.number(),
    monthlyLimit: z.number(),
    warning: z.boolean(),
    resetsAt: z.string(),
  })
  .passthrough();

export type AgentEmailSettings = z.infer<typeof AgentEmailSettingsSchema>;
export type AgentEmailQuota = z.infer<typeof AgentEmailQuotaSchema>;
export interface AgentEmailPatch {
  enabled?: boolean;
  allowedContacts?: string[];
  replyTo?: string | null;
}

export type NetworkEgressHost = z.infer<typeof NetworkEgressHostSchema>;
export type NetworkEgressConfig = z.infer<typeof NetworkEgressConfigSchema>;
export type NetworkEgressDenial = z.infer<typeof NetworkEgressDenialSchema>;
export type NetworkEgressRefreshFailure = z.infer<typeof NetworkEgressRefreshFailureSchema>;
export type NetworkEgressGetData = z.infer<typeof NetworkEgressGetDataSchema>;
export type NetworkEgressUpdateData = z.infer<typeof NetworkEgressUpdateDataSchema>;

export interface McpConnectParams {
  serverId: string;
  agentId: string;
  profileName: string;
  additionalScopes?: string[];
}

/**
 * Body for `POST /api/connectors/oauth/complete`. The SPA callback page reads
 * these from `window.location.search` and forwards them authenticated +
 * same-origin so the server can resolve the gateway from the live session.
 */
export interface OAuthCompletePayload {
  state: string;
  code?: string;
  error?: string;
  errorDescription?: string;
  connectedAccountId?: string;
  status?: "success" | "failed";
  rawQuery?: string;
}

// Re-exported from the canonical source so the UI types match the
// route-level enums without forcing the SPA to import from `../auth/`.
export type { UserRole } from "../auth/roles.js";
export { isUserRole } from "../auth/roles.js";

export interface UserCreatePayload {
  name: string;
  email: string;
  role: UserRole;
}

// Inbound response schemas are tolerant readers: every object is
// `.passthrough()`, never `.strict()`. A newer Hub adding an optional field to
// any of these responses must not turn the whole payload into an
// `INVALID_RESPONSE` — which surfaces as a page-level "Unable to load …". The
// server projections are additive by construction (e.g. `sanitizeCatalogEntry`
// spreads `{ ...rest }`), so a strict reader here is a latent landmine. Known
// fields are still validated; unknown ones ride through untouched. (Outbound
// request bodies are typed from the contracts' `.strict()` payload schemas.)
const CurrentOrgDataResponseSchema = z
  .object({
    org: z
      .object({
        id: z.string().min(1),
        slug: z.string().min(1),
        displayName: z.string().nullable(),
        logoUrl: z.string().nullable(),
        accentColor: z.string().nullable(),
        typefaceId: z.string().nullable(),
      })
      .passthrough(),
    membership: z
      .object({
        canCreateSpecialists: z.boolean(),
        canEditBranding: z.boolean(),
        // Application-side Administrator authority. Per-agent edit access
        // travels with each agent payload as `viewerCanEdit`.
        isAdmin: z.boolean(),
      })
      .passthrough(),
    hub: z
      .object({
        supportEmail: z.string().optional(),
        latestGatewayVersion: z.string().optional(),
      })
      .passthrough()
      .optional(),
    security: z.object({ bypassConfirmationsEnabled: z.boolean() }).passthrough().optional(),
  })
  .passthrough();

// The signed-in caller's identity. Served without the `{ ok, data }` envelope:
// a bare object on 200, a 401 status when unauthenticated — the shape the
// pre-portal auth gate branches on.
const MeDataResponseSchema = z
  .object({
    userId: z.string().min(1),
    email: z.string().min(1),
    displayName: z.string().nullable(),
    role: z.enum(USER_ROLES as unknown as [UserRole, ...UserRole[]]),
  })
  .passthrough();

// Public (unauthenticated) white-label payload for the pre-auth sign-in
// surface: it must render the tenant logo, accent cascade, and typeface before
// any session exists.
const InstanceBrandingDataResponseSchema = z
  .object({
    displayName: z.string().nullable(),
    logoUrl: z.string().nullable(),
    accentColor: z.string().nullable(),
    typefaceId: z.string().nullable(),
    supportEmail: z.string().nullable(),
  })
  .passthrough();

/** One Overview row: the conversation a specialist touched most recently. */
const DashboardRecentActivitySchema = z.object({
  agentId: z.string().min(1),
  sessionId: z.string(),
  sessionKey: z.string(),
  title: z.string(),
  platform: z.string(),
  status: z.enum(["active", "expired", "reset"]),
  accessedAt: z.string(),
});

const DashboardTodayActivityDataResponseSchema = z
  .object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    conversationCount: z.number().int().nonnegative(),
    /**
     * False when any specialist the caller took part with could not be read, so
     * the count above is a floor rather than a total.
     */
    countComplete: z.boolean(),
    /**
     * False when a specialist the caller can *open* could not be read, so the
     * rows below are short. Separate from `countComplete` because a specialist
     * the caller cannot open can fail without costing the feed anything.
     * Both are enumerated: an unnamed field is stripped here and the browser
     * would render a confident half-answer.
     */
    recentComplete: z.boolean(),
    /** Recency-ordered across every reachable specialist, then capped. */
    recent: z.array(DashboardRecentActivitySchema),
  })
  .passthrough();

export const DashboardPendingConfirmationSchema = z
  .object({
    confirmationId: z.string().min(1),
    agentId: z.string().min(1),
    toolName: z.string().min(1),
    toolId: z.string().optional(),
    requestedAt: z.string().datetime(),
    expiresAt: z.string().datetime().optional(),
    sessionKey: z.string().min(1),
    summary: z.string().optional(),
    preview: z.string().optional(),
    conversation: z
      .object({
        sessionId: z.string().min(1),
        platform: z.string().min(1),
      })
      .nullable(),
  })
  .passthrough();

const DecidedApprovalSchema = DashboardPendingConfirmationSchema.extend({
  state: z.enum(["approved", "denied", "expired", "cancelled"]),
  resolvedBy: z.string().optional(),
  resolvedAt: z.string().datetime(),
});

const QueuedApprovalSchema = DashboardPendingConfirmationSchema.extend({
  decision: z.enum(["approve", "deny"]),
  queuedAt: z.string().datetime(),
});

const DecidedApprovalsDataResponseSchema = z
  .object({
    approvals: z.array(DecidedApprovalSchema),
    queuedApprovals: z.array(QueuedApprovalSchema),
    trackedApprovals: z.array(DecidedApprovalSchema),
    pendingConfirmationIds: z.array(z.string().min(1)),
    retentionHours: z.number().positive(),
  })
  .passthrough();

const ResourceStatsResponseSchema = z
  .object({
    usedBytes: z.number().int().nonnegative(),
    totalBytes: z.number().int().nonnegative().optional(),
  })
  .passthrough();

const GatewayStatusDataResponseSchema = z
  .object({
    gatewayId: z.string().min(1),
    status: z.enum(["online", "offline"]),
    version: z.string().optional(),
    region: z.string().optional(),
    uptimeSeconds: z.number().nonnegative().optional(),
    agentCount: z.number().int().nonnegative().optional(),
    ram: ResourceStatsResponseSchema.extend({
      totalBytes: z.number().int().nonnegative(),
    }).optional(),
    disk: ResourceStatsResponseSchema.optional(),
  })
  .passthrough();

// Drives the API response zod parse. The tuple type assertion lets `z.enum`
// accept the readonly `USER_ROLES` tuple from `../auth/roles` while preserving
// the precise `UserRole` union in the inferred type (a `...string[]` tail would
// widen every derived `role` field to `string`).
const UserRoleSchema = z.enum(USER_ROLES as unknown as [UserRole, ...UserRole[]]);

const UserSummarySchema = z
  .object({
    id: z.string().min(1),
    email: z.string().min(1),
    displayName: z.string().nullable(),
    role: UserRoleSchema,
    specialistAccessCount: z.number().int().nonnegative(),
    lastSeenUpdatedAt: z.string().nullable().optional(),
    administration: z
      .object({
        demotion: z
          .object({
            reason: z.string().nullable(),
            requiresConfirmation: z.boolean(),
          })
          .passthrough(),
        removal: z.object({ reason: z.string().nullable() }).passthrough(),
      })
      .passthrough(),
  })
  .passthrough();

const UsersListResponseSchema = z
  .object({
    ok: z.literal(true),
    users: z.array(UserSummarySchema),
  })
  .passthrough();

const UserDetailResponseSchema = z
  .object({ ok: z.literal(true), user: UserSummarySchema })
  .passthrough();

const UserGrantSchema = z
  .object({
    agentId: z.string().min(1),
    specialistRole: z.enum(["owner", "editor", "user"]),
    grantedByUserId: z.string().nullable(),
    grantedAt: z.string().min(1),
    userId: z.string().optional(),
    orgId: z.string().optional(),
  })
  .passthrough();

const UserGrantsListResponseSchema = z
  .object({
    ok: z.literal(true),
    grants: z.array(UserGrantSchema),
  })
  .passthrough();

// Admin-created account. The server emails the new user a sign-in link; the
// response carries the persisted row only, never a credential.
const UserCreateResponseSchema = z
  .object({
    ok: z.literal(true),
    user: UserSummarySchema,
  })
  .passthrough();

const UserSetRoleResponseSchema = z
  .object({ ok: z.literal(true), role: UserRoleSchema })
  .passthrough();

const UserOkResponseSchema = z.object({ ok: z.literal(true) }).passthrough();

// API keys — programmatic access tokens minted by an admin or by the caller
// for themselves. The secret token appears exactly once, in the create
// response; list rows carry only the display prefix.
const ApiKeySummarySchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    tokenPrefix: z.string().min(1),
    userId: z.string().min(1),
    userEmail: z.string().min(1),
    createdAt: z.string().min(1),
    lastUsedAt: z.string().nullable(),
    revokedAt: z.string().nullable(),
  })
  .passthrough();

const ApiKeysListResponseSchema = z.object({ keys: z.array(ApiKeySummarySchema) }).passthrough();

const ApiKeyCreateResponseSchema = z
  .object({
    key: ApiKeySummarySchema,
    token: z.string().min(1),
  })
  .passthrough();

const ApiKeyRevokeResponseSchema = z.object({ ok: z.literal(true) }).passthrough();

const MeChannelSchema = z
  .object({
    platform: z.string().min(1),
    externalUserId: z.string().min(1),
    verified: z.boolean(),
    linkedAt: z.string().min(1),
    displayName: z.string().nullish(),
    teamId: z.string().nullish(),
    workspaceName: z.string().nullish(),
  })
  .passthrough();

const MeChannelsListResponseSchema = z
  .object({
    ok: z.literal(true),
    channels: z.array(MeChannelSchema),
  })
  .passthrough();

const SlackChannelStatusDataResponseSchema = z
  .object({
    installed: z.boolean(),
    oauthConfigured: z.boolean(),
    link: z
      .object({
        teamId: z.string().min(1),
        workspaceName: z.string().optional(),
        botUserId: z.string().optional(),
        installedByExternalUserId: z.string().optional(),
        installedBy: z.string().min(1),
        installedAt: z.string().min(1),
        updatedAt: z.string().min(1),
      })
      .passthrough()
      .optional(),
    credential: z
      .object({
        status: z.string().min(1),
        statusMessage: z.string().optional(),
        updatedAt: z.string().optional(),
        lastUsedAt: z.string().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

const GoogleChatChannelSchema = z.object({
  channel: z.literal("google-chat"),
  enabled: z.boolean(),
  active: z.boolean(),
  // Enumerated because this object strips what it does not name: without it the
  // instance's ability to store a credential would be dropped here and the
  // panel would go on offering management that cannot succeed.
  credentialStorage: z.enum(["available", "unavailable"]),
  endpointUrl: z.string().url(),
  credential: z
    .object({
      clientEmail: z.string().email(),
      projectId: z.string().nullable(),
      keyPresent: z.boolean(),
      addOnServiceAccountEmail: z.string().nullable(),
      installedAt: z.string(),
    })
    .nullable(),
});
const ChannelListDataResponseSchema = z.object({ channels: z.array(GoogleChatChannelSchema) });
const GoogleChatChannelDataResponseSchema = z.object({ channel: GoogleChatChannelSchema });

// Result of `POST /api/connectors/oauth/complete`. `outcome:"success"` carries
// the binding identity needed to deep-link to the agent's connectors tab;
// `outcome:"error"` carries a UI-facing `reason` (and optional scopes for the
// scope-consent flow).
const OAuthCompleteDataResponseSchema = z.discriminatedUnion("outcome", [
  z
    .object({
      ok: z.literal(true),
      outcome: z.literal("success"),
      agentId: z.string().min(1),
      serverId: z.string().min(1),
      profileName: z.string().min(1),
    })
    .passthrough(),
  z
    .object({
      ok: z.literal(true),
      outcome: z.literal("error"),
      reason: z.string().min(1),
      scopes: z.array(z.string()).optional(),
    })
    .passthrough(),
]);

const ConnectorCategorySchema = z.enum([
  "support",
  "ecommerce",
  "productivity",
  "analytics",
  "dev",
  "communication",
  "marketing",
]);

const ConnectorKindSchema = z.enum(["mcp_oauth", "mcp_api_key", "mcp_stdio", "module", "composio"]);

const ConnectorModuleFieldResponseSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    type: z.enum(["text", "password"]),
    required: z.boolean(),
    placeholder: z.string().optional(),
    helpText: z.string().optional(),
  })
  .passthrough();

const ConnectorApiKeyFieldResponseSchema = ConnectorModuleFieldResponseSchema.extend({
  type: z.enum(["text", "password", "subdomain"]),
}).passthrough();

const ConnectorInstallModeSubmitSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("credentials"), button: z.string().min(1) }).passthrough(),
  z.object({ kind: z.literal("oauth"), button: z.string().min(1) }).passthrough(),
]);

const ConnectorInstallModeResponseSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    description: z.string().optional(),
    fields: z.array(z.string().min(1)),
    submit: ConnectorInstallModeSubmitSchema,
  })
  .passthrough();

const ConnectorCatalogEntryResponseSchema = z
  .object({
    id: z.string().min(1),
    displayName: z.string().min(1),
    description: z.string(),
    domain: z.string().min(1),
    category: ConnectorCategorySchema,
    kind: ConnectorKindSchema,
    enabled: z.boolean(),
    defaultProfileName: z.string().min(1),
    /**
     * How Hub starts a connection for this entry, decided by the Gateway:
     * `credentials` collects field values in Hub's form, `oauth_redirect`
     * sends the browser to the OAuth start route.
     *
     * Required, like every other field the panel reads to render and route a
     * row. This schema's tolerance is `passthrough` for fields it does not
     * enumerate, not optionality for the ones it acts on; the instance builds
     * and serves this SPA, so a catalog entry without this field means a
     * mismatched build, and naming it in a failed response beats defaulting
     * every connector to one flow.
     */
    connectFlow: z.enum(["credentials", "oauth_redirect"]),
    fields: z.array(ConnectorModuleFieldResponseSchema).optional(),
    installModes: z.array(ConnectorInstallModeResponseSchema).optional(),
    oauth: z
      .object({
        scopes: z.array(z.string()),
        additionalScopesAllowed: z.array(z.string()).optional(),
      })
      .passthrough()
      .optional(),
    apiKey: z
      .object({
        fields: z.array(ConnectorApiKeyFieldResponseSchema),
        validateEndpoint: z
          .object({
            method: z.enum(["GET", "POST"]),
            url: z.string().optional(),
            path: z.string().optional(),
            headerTemplates: z.record(z.string(), z.string()).optional(),
            bodyTemplate: z.unknown().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough()
      .optional(),
    mcp: z
      .object({
        transport: z.literal("streamable_http"),
        urlTemplate: z.string().min(1),
      })
      .passthrough()
      .optional(),
    composio: z
      .object({
        toolkit: z.string().min(1),
        extraAuthParams: z.array(z.string()).optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

const ConnectorProfileStatusSchema = z.enum([
  "active",
  "auth_required",
  "needs_reconnection",
  "scope_consent_required",
  "discovery_failed",
  "disabled",
  "disconnecting",
]);

const ConnectorManageActionResponseSchema = z
  .object({
    label: z.string().min(1),
    url: z.string().url(),
  })
  .passthrough();

const ConnectorProfileResponseSchema = z
  .object({
    serverId: z.string().min(1),
    profileName: z.string().min(1),
    displayName: z.string().min(1),
    createdAt: z.string().min(1),
    status: ConnectorProfileStatusSchema,
    statusReason: z.string().optional(),
    toolCount: z.number().int().nonnegative().optional(),
    /**
     * The administrator turned this connector type off instance-wide. The row
     * and its credentials persist; only disconnect remains available, and
     * re-enabling restores service without reconnecting.
     */
    connectorDisabled: z.boolean().optional(),
    manageAction: ConnectorManageActionResponseSchema.optional(),
  })
  .passthrough();

const ConnectorLifecycleProfileResponseSchema = z
  .object({
    serverId: z.string().min(1),
    profileName: z.string().min(1),
    displayName: z.string().min(1),
    createdAt: z.string().min(1).nullable(),
  })
  .passthrough();

const ConnectorPersistedProfileResponseSchema = z
  .object({
    serverId: z.string().min(1),
    profileName: z.string().min(1),
    displayName: z.string().min(1),
    createdAt: z.string().min(1),
  })
  .passthrough();

const ConnectorCatalogDataResponseSchema = z
  .object({
    entries: z.array(ConnectorCatalogEntryResponseSchema),
  })
  .passthrough();

const ConnectorAvailabilityResponseSchema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("available") }).passthrough(),
  z
    .object({
      state: z.literal("needs_configuration"),
      missing: z.enum(["oauth_client", "env"]),
      fields: z.array(z.string()),
      envVars: z.array(z.string()).optional(),
    })
    .passthrough(),
  z
    .object({
      state: z.literal("unavailable"),
      reason: z.literal("composio_broker_missing"),
    })
    .passthrough(),
]);

const ConnectorAdminRowResponseSchema = z
  .object({
    id: z.string().min(1),
    displayName: z.string().min(1),
    description: z.string(),
    domain: z.string().min(1),
    category: z.string().min(1),
    kind: z.string().min(1),
    buildDefaultEnabled: z.boolean(),
    effectiveEnabled: z.boolean(),
    override: z.object({ enabled: z.boolean() }).passthrough().optional(),
    availability: ConnectorAvailabilityResponseSchema,
    /**
     * Whether the config routes apply to this type. The panel renders its
     * config affordance from this, so no visible control can lead to a save
     * with nowhere to go.
     */
    acceptsOAuthClient: z.boolean().optional(),
    /** Non-secret half of a stored OAuth client. The secret has no read path. */
    configured: z
      .object({ clientId: z.string(), appHandle: z.string().optional() })
      .passthrough()
      .optional(),
  })
  .passthrough();

const ConnectorAdminListDataResponseSchema = z
  .object({ connectors: z.array(ConnectorAdminRowResponseSchema) })
  .passthrough();

const ConnectorAdminRowDataResponseSchema = z
  .object({
    connector: ConnectorAdminRowResponseSchema,
    changed: z.boolean().optional(),
  })
  .passthrough();

const ConnectorListDataResponseSchema = z
  .object({
    profiles: z.array(ConnectorProfileResponseSchema),
    available: z.array(ConnectorCatalogEntryResponseSchema),
  })
  .passthrough();

const ConnectorFormFieldResponseSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    type: z.string().min(1),
    required: z.boolean(),
  })
  .passthrough();

const ConnectorConnectDataResponseSchema = z
  .object({
    profile: ConnectorLifecycleProfileResponseSchema,
    kind: ConnectorKindSchema,
    redirectUrl: z.string().optional(),
    fields: z.array(ConnectorFormFieldResponseSchema).optional(),
  })
  .passthrough();

const ConnectorCredentialsDataResponseSchema = z
  .object({
    ok: z.literal(true),
    credentialId: z.string().optional(),
    profile: ConnectorPersistedProfileResponseSchema.optional(),
  })
  .passthrough();

const ConnectorDiscoverDataResponseSchema = z
  .object({
    ok: z.literal(true),
    discoveredAt: z.string().min(1),
  })
  .passthrough();

const VisibilityDeltaResponseSchema = z
  .object({
    added: z.array(z.string()),
    removed: z.array(z.string()),
  })
  .passthrough();

const ConnectorVisibilityDataResponseSchema = z
  .object({
    ok: z.literal(true),
    visibility: VisibilityDeltaResponseSchema,
  })
  .passthrough();

const ConnectorReconnectDataResponseSchema = z
  .object({
    kind: ConnectorKindSchema,
    profileName: z.string().min(1),
    entry: ConnectorCatalogEntryResponseSchema,
    redirectUrl: z.string().optional(),
    fields: z.array(ConnectorFormFieldResponseSchema).optional(),
  })
  .passthrough();

const ConnectorRenameDataResponseSchema = z.object({ ok: z.literal(true) }).passthrough();

const ConnectorToolResponseSchema = z
  .object({
    toolId: z.string().min(1),
    description: z.string().optional(),
  })
  .passthrough();

const ConnectorPermissionsDataResponseSchema = z
  .object({
    sources: z.array(
      z
        .object({
          serverId: z.string().min(1),
          tools: z.array(ConnectorToolResponseSchema),
        })
        .passthrough(),
    ),
    profiles: z.array(
      z
        .object({
          serverId: z.string().min(1),
          profileName: z.string().min(1),
          displayName: z.string().min(1),
          discovery: z
            .object({
              status: z.enum(["not_inspected", "failed", "present"]),
              reason: z.string().optional(),
            })
            .strict(),
          permissions: z
            .object({
              allow: z.array(z.string()),
              ask: z.array(z.string()),
            })
            .passthrough(),
        })
        .passthrough(),
    ),
    builtins: z
      .object({
        tools: z.array(ConnectorToolResponseSchema),
        permissions: z
          .object({
            allow: z.array(z.string()),
            ask: z.array(z.string()),
            deny: z.array(z.string()),
          })
          .passthrough(),
      })
      .passthrough(),
    gatewayTools: z
      .object({
        tools: z.array(ConnectorToolResponseSchema),
        permissions: z
          .object({
            allow: z.array(z.string()),
            ask: z.array(z.string()),
            deny: z.array(z.string()),
          })
          .passthrough(),
      })
      .passthrough(),
    browser: z
      .object({
        tools: z.array(ConnectorToolResponseSchema),
        permissions: z
          .object({
            allow: z.array(z.string()),
            ask: z.array(z.string()),
            deny: z.array(z.string()),
          })
          .passthrough(),
      })
      .passthrough()
      .optional(),
    memory: z
      .object({
        sourceId: z.string().min(1),
        displayName: z.string().min(1),
        tools: z.array(ConnectorToolResponseSchema),
        permissions: z
          .object({
            allow: z.array(z.string()),
            ask: z.array(z.string()),
            deny: z.array(z.string()),
          })
          .passthrough(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

const OrgBrandingDataResponseSchema = z
  .object({
    displayName: z.string().nullable(),
    logoUrl: z.string().nullable(),
    accentColor: z.string().nullable(),
    typefaceId: z.string().nullable(),
  })
  .passthrough();

// Agent avatar upload/remove — Hub-internal HTTP shapes (never cross the
// Gateway wire): the POST returns the freshly-minted public URL, the DELETE
// clears it to `null`.
const AvatarUploadDataResponseSchema = z.object({ iconUrl: z.string().min(1) }).passthrough();
const AvatarDeleteDataResponseSchema = z.object({ iconUrl: z.null() }).passthrough();

// Dashboards — the operator dashboards surface. These routes carry their payload
// under a named key (`dashboards`/`dashboard`/`run`/`component`/`grants`) beside
// `ok`, not the `{ ok, data }` envelope, so they are read with the "flat"
// envelope like the identity routes. Tolerant readers (`.passthrough()`): a new
// server field must not hard-fail the page.
// Only the fields the pages actually read are required; the gateway-sourced
// extras (createdAt/updatedAt/durationMs/…) stay optional so a partial or
// evolving upstream shape renders rather than hard-failing the whole page.
const DashboardSummarySchema = z
  .object({
    dashboardId: z.string(),
    agentId: z.string(),
    name: z.string(),
    visibility: AssetVisibilitySchema,
    viewerCanChangeVisibility: z.boolean(),
    health: DashboardHealthSchema,
    description: z.string().optional(),
    agentName: z.string().optional(),
    agentRole: z.string().optional(),
    agentAvatarUrl: z.string().nullish(),
    createdAt: z.number().optional(),
    updatedAt: z.number().optional(),
    lastRunAt: z.number().optional(),
  })
  .passthrough();

const DashboardComponentSchema = z
  .object({
    id: z.string(),
    kind: DashboardComponentKindSchema,
    title: z.string(),
    size: DashboardComponentSizeSchema,
    description: z.string().optional(),
    lastRunStatus: z.string().optional(),
    lastRunAt: z.number().optional(),
    lastError: z.string().optional(),
  })
  .passthrough();

const DashboardDetailSchema = z
  .object({
    dashboardId: z.string(),
    agentId: z.string(),
    name: z.string(),
    components: z.array(DashboardComponentSchema),
    visibility: AssetVisibilitySchema,
    viewerCanChangeVisibility: z.boolean(),
    description: z.string().optional(),
    health: DashboardHealthSchema.optional(),
    createdAt: z.number().optional(),
    updatedAt: z.number().optional(),
  })
  .passthrough();

// Per-component run outcome. `ok` here is the component's own success flag, not
// the HTTP envelope's; `result` is the kind-specific payload the tile renders.
const DashboardComponentRunSchema = z
  .object({
    componentId: z.string(),
    ok: z.boolean(),
    result: z.unknown().optional(),
    lastResultPreview: z.string().optional(),
    errorKind: z.string().optional(),
    error: z.string().optional(),
    lastRunAt: z.number().optional(),
    materialisedAt: z.number().optional(),
    durationMs: z.number().optional(),
    toolCallsMade: z.number().optional(),
    logs: z.array(z.string()).optional(),
  })
  .passthrough();

const DashboardRunSchema = z
  .object({
    components: z.array(DashboardComponentRunSchema),
    status: z.string().optional(),
  })
  .passthrough();

const DashboardGrantSchema = z
  .object({
    userId: z.string(),
    role: z.string(),
    grantedAt: z.string(),
    grantedByUserId: z.string().nullish(),
  })
  .passthrough();

const DashboardListResponseSchema = z
  .object({ ok: z.literal(true), dashboards: z.array(DashboardSummarySchema) })
  .passthrough();

const DashboardGetResponseSchema = z
  .object({ ok: z.literal(true), dashboard: DashboardDetailSchema })
  .passthrough();

const DashboardRunResponseSchema = z
  .object({ ok: z.literal(true), run: DashboardRunSchema })
  .passthrough();

const DashboardComponentRunResponseSchema = z
  .object({ ok: z.literal(true), component: DashboardComponentRunSchema })
  .passthrough();

const DashboardGrantsListResponseSchema = z
  .object({ ok: z.literal(true), grants: z.array(DashboardGrantSchema) })
  .passthrough();

const DashboardGrantMutationResponseSchema = z.object({ ok: z.literal(true) }).passthrough();
const AssetVisibilityResponseSchema = z.object({ visibility: AssetVisibilitySchema }).passthrough();
const DashboardDeleteResponseSchema = z.object({ ok: z.literal(true) }).passthrough();

// Response types — single source. Each is derived from its `.passthrough()`
// response schema above via `z.infer`; the `HubApiClient` interface methods and
// the impl below reference these names, so a wire shape lives in exactly one
// place (the schema) instead of being restated as an interface, an inline
// object literal, and a `request<{…}>` type argument.
export type CurrentOrgData = z.infer<typeof CurrentOrgDataResponseSchema>;
export type MeData = z.infer<typeof MeDataResponseSchema>;
export type InstanceBrandingData = z.infer<typeof InstanceBrandingDataResponseSchema>;
export type ApiKeySummary = z.infer<typeof ApiKeySummarySchema>;
export type ApiKeysListData = z.infer<typeof ApiKeysListResponseSchema>;
export type ApiKeyCreateData = z.infer<typeof ApiKeyCreateResponseSchema>;
export type DashboardTodayActivityData = z.infer<typeof DashboardTodayActivityDataResponseSchema>;
export type DashboardRecentActivity = z.infer<typeof DashboardRecentActivitySchema>;
export type DashboardPendingConfirmation = z.infer<typeof DashboardPendingConfirmationSchema>;
export type DecidedApproval = z.infer<typeof DecidedApprovalSchema>;
export type QueuedApproval = z.infer<typeof QueuedApprovalSchema>;
export type DecidedApprovalsData = z.infer<typeof DecidedApprovalsDataResponseSchema>;
export type GatewayStatusData = z.infer<typeof GatewayStatusDataResponseSchema>;
export type OAuthCompleteData = z.infer<typeof OAuthCompleteDataResponseSchema>;
export type UsersListData = z.infer<typeof UsersListResponseSchema>;
export type UserSummary = z.infer<typeof UserSummarySchema>;
export type UserDetailData = z.infer<typeof UserDetailResponseSchema>;
export type UserGrantsListData = z.infer<typeof UserGrantsListResponseSchema>;
export type UserCreateData = z.infer<typeof UserCreateResponseSchema>;
export type UserSetRoleData = z.infer<typeof UserSetRoleResponseSchema>;
export type MeChannelsListData = z.infer<typeof MeChannelsListResponseSchema>;
export type SlackChannelStatusData = z.infer<typeof SlackChannelStatusDataResponseSchema>;
export type GoogleChatChannel = z.infer<typeof GoogleChatChannelSchema>;
export type OrgBrandingData = z.infer<typeof OrgBrandingDataResponseSchema>;
export type AvatarUploadData = z.infer<typeof AvatarUploadDataResponseSchema>;
export type AvatarDeleteData = z.infer<typeof AvatarDeleteDataResponseSchema>;

// Connector surface — the shapes that were previously restated 3–4× (exported
// view interface, interface-method literal, `request<{…}>` argument, Zod
// mirror). Now: one schema, one `z.infer`.
export type ConnectorCatalogEntryView = z.infer<typeof ConnectorCatalogEntryResponseSchema>;
export type ConnectorAvailability = z.infer<typeof ConnectorAvailabilityResponseSchema>;
export type ConnectorAdminRow = z.infer<typeof ConnectorAdminRowResponseSchema>;
export type ConnectorAdminListData = z.infer<typeof ConnectorAdminListDataResponseSchema>;
export type ConnectorAdminRowData = z.infer<typeof ConnectorAdminRowDataResponseSchema>;
export type ConnectorCatalogData = z.infer<typeof ConnectorCatalogDataResponseSchema>;
export type ConnectorListData = z.infer<typeof ConnectorListDataResponseSchema>;
export type ConnectorConnectData = z.infer<typeof ConnectorConnectDataResponseSchema>;
export type ConnectorCredentialsData = z.infer<typeof ConnectorCredentialsDataResponseSchema>;
export type ConnectorDiscoverData = z.infer<typeof ConnectorDiscoverDataResponseSchema>;
export type ConnectorVisibilityData = z.infer<typeof ConnectorVisibilityDataResponseSchema>;
export type ConnectorReconnectData = z.infer<typeof ConnectorReconnectDataResponseSchema>;
export type ConnectorRenameData = z.infer<typeof ConnectorRenameDataResponseSchema>;
export type ConnectorPermissionsData = z.infer<typeof ConnectorPermissionsDataResponseSchema>;

// Dashboards — one schema, one `z.infer`. The pages import the row/run/grant
// shapes; the `*Data` types are the flat envelopes the client methods return.
export type DashboardSummary = z.infer<typeof DashboardSummarySchema>;
export type DashboardComponentRun = z.infer<typeof DashboardComponentRunSchema>;
export type DashboardGrant = z.infer<typeof DashboardGrantSchema>;
export type DashboardListData = z.infer<typeof DashboardListResponseSchema>;
export type DashboardGetData = z.infer<typeof DashboardGetResponseSchema>;
export type DashboardRunData = z.infer<typeof DashboardRunResponseSchema>;
export type DashboardComponentRunData = z.infer<typeof DashboardComponentRunResponseSchema>;
export type DashboardGrantsListData = z.infer<typeof DashboardGrantsListResponseSchema>;
export type DashboardGrantMutationData = z.infer<typeof DashboardGrantMutationResponseSchema>;
export type DashboardDeleteData = z.infer<typeof DashboardDeleteResponseSchema>;

export interface HubApiClientOptions {
  baseUrl?: string;
  fetch?: typeof fetch;
  headers?: HeadersInit | (() => HeadersInit | Promise<HeadersInit>);
  /**
   * Invoked when an `/api/*` response is a 401. The UI wires this to clear
   * caches and sign the session out so a stale session surfaces as signed-out
   * and routes to `/login`. The 401 is still thrown as a `HubApiError`
   * afterwards so callers behave normally.
   */
  onUnauthorized?: () => void;
}

interface HttpSuccessEnvelope {
  ok: true;
  data: unknown;
}

interface HttpErrorEnvelope {
  ok: false;
  error: string;
  errorCode: string;
  requestId?: string;
  details?: unknown;
}

type HttpEnvelope = HttpSuccessEnvelope | HttpErrorEnvelope;

export interface OrgBrandingPayload {
  displayName?: string | null;
  accentColor?: string | null;
  typefaceId?: string | null;
}

// Hub-chat message-send ACK. The route returns the standard `{ ok, data }`
// envelope with the send outcome under `data`; this reads the whole envelope
// ("raw") so the live surface can still branch on the inner `data.ok` (a
// dropped send) and the `/new` reset (`outcome:"slash"` + `slash.newSessionId`).
// Tolerant reader: the gateway may add fields to the ack.
const HubChatSendResponseSchema = z
  .object({
    ok: z.boolean(),
    data: z
      .object({
        ok: z.boolean().optional(),
        outcome: z.string().optional(),
        slash: z
          .object({
            kind: z.string().optional(),
            newSessionId: z.string().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough(),
  })
  .passthrough();

export type HubChatSendResult = z.infer<typeof HubChatSendResponseSchema>;

/** Attachment reference for a send; the shape the upload endpoint returns. */
export interface HubChatSendAttachment {
  objectKey: string;
  name: string;
  mimeType: string;
  size: number;
  status: string;
}

export interface HubChatSendInput {
  sessionId: string;
  text: string;
  clientMessageId: string;
  attachments?: readonly HubChatSendAttachment[];
}

export interface HubApiClient {
  request<TData>(path: string, init?: RequestInit): Promise<TData>;
  /** Public pre-auth white-label payload; needs no session. */
  instanceBranding(): Promise<InstanceBrandingData>;
  org: {
    current(): Promise<CurrentOrgData>;
    updateBranding(payload: OrgBrandingPayload): Promise<OrgBrandingData>;
    uploadLogo(file: File): Promise<OrgBrandingData>;
    deleteLogo(): Promise<OrgBrandingData>;
    updateSecurity(enabled: boolean): Promise<{ bypassConfirmationsEnabled: boolean }>;
  };
  gateway: {
    status(): Promise<GatewayStatusData>;
    restart(): Promise<RestartData>;
    eventsUrl(): string;
  };
  channels: {
    list(): Promise<{ channels: GoogleChatChannel[] }>;
    googleChat: {
      install(input: {
        serviceAccountJson: string;
        addOnServiceAccountEmail: string;
      }): Promise<{ channel: GoogleChatChannel }>;
      setEnabled(enabled: boolean): Promise<{ channel: GoogleChatChannel }>;
      remove(): Promise<{ channel: GoogleChatChannel }>;
    };
    slack: {
      status(): Promise<SlackChannelStatusData>;
    };
  };
  /** Agent Teams registry. Admin-only end to end. */
  agentTeams: {
    list(): Promise<{ teams: AgentTeam[] }>;
    create(payload: { name: string; description?: string }): Promise<{ team: AgentTeam }>;
    update(
      agentTeamId: string,
      payload: { name?: string; description?: string },
    ): Promise<{ team: AgentTeam }>;
    delete(agentTeamId: string): Promise<{ ok: true }>;
  };
  usage: {
    /** Admin-only. Period totals + per-day/agent/user/model usage aggregates. */
    summary(payload: UsageSummaryPayload): Promise<UsageSummaryData>;
  };
  dashboard: {
    /** Org-wide count only; conversation summaries remain server-side. */
    activityToday(): Promise<DashboardTodayActivityData>;
    /** Replay-then-stream queue narrowed to confirmations the current operator may manage. */
    pendingConfirmationsEventsUrl(): string;
    /** Terminal confirmations still inside the bounded retention window. */
    decidedApprovals(confirmationIds?: readonly string[]): Promise<DecidedApprovalsData>;
    resolvePendingConfirmation(input: {
      agentId: string;
      sessionId: string;
      confirmationId: string;
      decision: "approve" | "deny";
    }): Promise<HubChatConfirmAck>;
  };
  applications: {
    list(): Promise<ApplicationsListData>;
    getInternal(appId: string): Promise<InternalApplicationView>;
    createSession(appId: string): Promise<ApplicationSessionTicket>;
    sessionSocketUrl(socketPath: string, pageHref: string): string;
    runAction(
      appId: string,
      actionName: ApplicationActionName,
      args: unknown,
    ): Promise<ApplicationActionResult>;
    updateVisibility(
      appId: string,
      visibility: z.infer<typeof AssetVisibilitySchema>,
    ): Promise<z.infer<typeof AssetVisibilityResponseSchema>>;
    createExternal(input: {
      title: string;
      url: string;
      icon?: ApplicationIconKey;
    }): Promise<ExternalApplicationView>;
    updateExternal(
      id: string,
      input: { title?: string; url?: string; icon?: ApplicationIconKey | null },
    ): Promise<ExternalApplicationView>;
    deleteInternal(appId: string): Promise<{ deleted: boolean }>;
    deleteExternal(id: string): Promise<{ deleted: boolean }>;
  };
  agents: {
    list(): Promise<AgentListData>;
    /** Archived agents for the admin recovery surface. */
    listArchived(): Promise<AgentArchivedListData>;
    templates(): Promise<SpecialistTemplatesData>;
    get(agentId: string): Promise<AgentGetData>;
    chatTarget(agentId: string): Promise<AgentChatTargetData>;
    create(payload: AgentCreateInput): Promise<AgentGetData>;
    update(agentId: string, payload: Omit<AgentUpdateInput, "agentId">): Promise<AgentUpdateData>;
    /** Shadow-delete: retires the agent, retains its record/id, purges nothing. */
    delete(agentId: string): Promise<AgentDeleteData>;
    archive(agentId: string): Promise<AgentArchiveData>;
    unarchive(agentId: string): Promise<AgentUnarchiveData>;
    uploadAvatar(agentId: string, file: File): Promise<AvatarUploadData>;
    deleteAvatar(agentId: string): Promise<AvatarDeleteData>;
    /** Null when the agent is Custom or its template left the registry. */
    templateDiff(agentId: string): Promise<TemplateDiffData>;
    templateApply(
      agentId: string,
      payload: { forcePromptOverwrite: boolean },
    ): Promise<TemplateApplyData>;
  };
  credentials: {
    list(agentId?: string): Promise<CredentialListData>;
    set(payload: CredentialSetPayload): Promise<CredentialSetData>;
    update(
      namespace: string,
      key: string,
      payload: Omit<CredentialUpdatePayload, "namespace" | "key">,
    ): Promise<CredentialUpdateData>;
    delete(namespace: string, key: string, agentId?: string): Promise<CredentialDeleteData>;
  };
  mcp: {
    connectUrl(params: McpConnectParams): string;
  };
  /** Connector Catalog and capability-backed lifecycle routes. */
  connectors: {
    catalog(): Promise<ConnectorCatalogData>;
    list(agentId: string): Promise<ConnectorListData>;
    /** Instance connector administration. Admin-only; 403 otherwise. */
    admin: {
      list(): Promise<ConnectorAdminListData>;
      setEnabled(connectorType: string, enabled: boolean): Promise<ConnectorAdminRowData>;
      /** Secret material is accepted and never returned. */
      saveConfig(
        connectorType: string,
        payload: { clientId: string; clientSecret: string; appHandle?: string },
      ): Promise<ConnectorAdminRowData>;
      deleteConfig(connectorType: string): Promise<ConnectorAdminRowData>;
    };
    connect(payload: {
      agentId: string;
      connectorId: string;
      profileName?: string;
      displayName?: string;
    }): Promise<ConnectorConnectData>;
    setCredentials(payload: {
      agentId: string;
      connectorId: string;
      profileName?: string;
      displayName?: string;
      fields: Record<string, string>;
    }): Promise<ConnectorCredentialsData>;
    discover(payload: {
      agentId: string;
      connectorId: string;
      profileName?: string;
    }): Promise<ConnectorDiscoverData>;
    disconnect(payload: {
      agentId: string;
      connectorId: string;
      profileName: string;
    }): Promise<ConnectorVisibilityData>;
    reconnect(payload: {
      agentId: string;
      connectorId: string;
      profileName: string;
      displayName?: string;
    }): Promise<ConnectorReconnectData>;
    rename(payload: {
      agentId: string;
      connectorId: string;
      profileName: string;
      displayName: string;
    }): Promise<ConnectorRenameData>;
    permissions: {
      get(agentId: string): Promise<ConnectorPermissionsData>;
      set(payload: {
        agentId: string;
        scope: "profile" | "builtin" | "gateway-tool" | "browser" | "memory";
        serverId?: string;
        profileName?: string;
        sourceId?: string;
        toolId: string;
        state: "deny" | "ask" | "allow";
      }): Promise<ConnectorVisibilityData>;
    };
    /**
     * Completes an MCP/connector OAuth flow. Called by the SPA callback page
     * with the `code`/`state` it read from `window.location.search`. The
     * request is authenticated + same-origin so the server resolves the
     * gateway from the live session. The optional `signal` lets the
     * caller bound the wait — the callback page aborts a hung handshake so the
     * user never sees an infinite spinner.
     */
    completeOAuth(payload: OAuthCompletePayload, signal?: AbortSignal): Promise<OAuthCompleteData>;
  };
  permissions: {
    list(agentId: string): Promise<PermissionListData>;
    set(
      agentId: string,
      tool: string,
      payload: Omit<PermissionSetPayload, "agentId" | "tool">,
    ): Promise<PermissionSetData>;
  };
  memory: {
    list(
      agentId: string,
      params?: { topic?: string; tag?: string; limit?: number; cursor?: string },
    ): Promise<MemoryListByFilterData>;
    read(agentId: string, handle: string): Promise<MemoryReadByHandleData>;
    write(
      agentId: string,
      handle: string,
      input: { description: string; content: string; topic: string; tags?: string[] },
    ): Promise<MemoryWriteByHandleData>;
    delete(agentId: string, handle: string): Promise<MemoryDeleteByHandleData>;
    downloadUrl(agentId: string, handle: string): string;
    topics: {
      list(agentId: string): Promise<MemoryListTopicsData>;
      save(
        agentId: string,
        input: { id: string; label: string; description: string },
      ): Promise<MemorySaveTopicData>;
      delete(agentId: string, id: string): Promise<MemoryDeleteTopicData>;
      rename(agentId: string, id: string, newId: string): Promise<MemoryRenameTopicData>;
    };
    tags: {
      list(agentId: string): Promise<MemoryListTagsData>;
      save(
        agentId: string,
        input: { name: string; description?: string },
      ): Promise<MemorySaveTagData>;
      delete(agentId: string, name: string): Promise<MemoryDeleteTagData>;
      rename(agentId: string, name: string, newName: string): Promise<MemoryRenameTagData>;
    };
  };
  workspace: {
    list(agentId: string, path?: string): Promise<WorkspaceListData>;
    read(agentId: string, path: string): Promise<WorkspaceReadData>;
    writeText(
      agentId: string,
      path: string,
      content: string,
      contentType?: string,
    ): Promise<WorkspaceWriteData>;
    upload(
      agentId: string,
      folderPath: string,
      file: File,
      overwrite?: boolean,
    ): Promise<WorkspaceWriteData>;
    delete(agentId: string, path: string): Promise<WorkspaceDeleteData>;
    mkdir(agentId: string, path: string): Promise<WorkspaceMkdirData>;
    downloadUrl(agentId: string, path: string): string;
  };
  skills: {
    list(agentId: string): Promise<SkillListData>;
    add(agentId: string, skillId: string): Promise<SkillAddData>;
    remove(agentId: string, skillId: string): Promise<SkillRemoveData>;
    configure(
      agentId: string,
      skillId: string,
      config: Record<string, unknown>,
    ): Promise<SkillConfigureData>;
    files(agentId: string, skillId: string): Promise<SkillFilesListData>;
    readFile(agentId: string, skillId: string, path: string): Promise<SkillFilesReadData>;
    upload(agentId: string, form: FormData): Promise<SkillUploadData>;
    uploadFile(
      agentId: string,
      skillId: string,
      path: string,
      file: File,
      overwrite?: boolean,
    ): Promise<SkillFilesWriteData>;
    downloadFileUrl(agentId: string, skillId: string, path: string): string;
  };
  schedules: {
    list(agentId: string): Promise<ScheduleListData>;
    runs(agentId: string, scheduleId: string, limit?: number): Promise<ScheduleRunsData>;
    transcript(
      agentId: string,
      scheduleId: string,
      runId: number,
    ): Promise<ScheduleRunTranscriptData>;
    update(agentId: string, scheduleId: string, patch: SchedulePatch): Promise<ScheduleUpdateData>;
    action(
      agentId: string,
      scheduleId: string,
      action: ScheduleActionPayload["action"],
    ): Promise<ScheduleActionData>;
  };
  browser: {
    get(agentId: string): Promise<BrowserStatus>;
    configure(agentId: string, enabled: boolean): Promise<BrowserStatus>;
    forget(agentId: string, origin: string): Promise<BrowserStatus>;
    reset(agentId: string): Promise<BrowserStatus>;
    ticket(agentId: string): Promise<{ ticket: string }>;
    handoff(
      agentId: string,
      input: { confirmationId: string; outcome: "done" | "failed"; note?: string },
    ): Promise<{ outcome: string }>;
  };
  networkEgress: {
    get(agentId: string): Promise<NetworkEgressGetData>;
    update(agentId: string, config: NetworkEgressConfig): Promise<NetworkEgressUpdateData>;
  };
  email: {
    get(agentId: string): Promise<AgentEmailSettings>;
    update(agentId: string, patch: AgentEmailPatch): Promise<AgentEmailSettings>;
    quota(agentId: string): Promise<AgentEmailQuota>;
  };
  conversations: {
    create(
      agentId: string,
      sessionId: string,
    ): Promise<z.infer<typeof ConversationCreateDataSchema>>;
    resolve(
      agentId: string,
      sessionId: string,
      address?: "record" | "channel",
    ): Promise<z.infer<typeof ConversationResolutionDataSchema>>;
    list(
      agentId: string,
      opts?: { archived?: "only"; kind?: ConversationKindFilter },
    ): Promise<SessionListData>;
    history(
      agentId: string,
      sessionId: string,
      opts?: { limit?: number; before?: string; address?: "record" | "channel" },
    ): Promise<ConversationPage>;
    /**
     * Submit a hub-chat message. Resolves the whole `{ ok, data }` envelope so
     * the live surface reads the send outcome (dropped send, `/new` reset); an
     * HTTP failure rejects as a `HubApiError`.
     */
    send(agentId: string, input: HubChatSendInput): Promise<HubChatSendResult>;
    /**
     * Move a conversation out of the lists, or back into them. Conversation-
     * wide, not per-viewer: anyone who can see it may archive it, and it then
     * disappears for everyone. Nothing is deleted, and new activity on an
     * archived conversation brings it back on its own.
     */
    setArchived(
      agentId: string,
      sessionId: string,
      input: { archived: boolean; address?: "record" | "channel" },
    ): Promise<{ archived: boolean }>;
    setConfirmationMode(
      agentId: string,
      sessionId: string,
      confirmationMode: "manual" | "bypass",
    ): Promise<{ confirmationMode: "manual" | "bypass" }>;
    participants: {
      list(agentId: string, sessionId: string): Promise<ConversationParticipantsData>;
      invite(
        agentId: string,
        sessionId: string,
        userId: string,
      ): Promise<ConversationParticipantsData>;
      leave(agentId: string, sessionId: string): Promise<ConversationLeaveData>;
    };
    audience(agentId: string): Promise<ConversationAudienceData>;
  };
  /**
   * Cross-agent conversation index for the caller's Workspace page. A flat
   * method rather than a `workspace` namespace: that name already belongs to
   * the per-agent file workspace above.
   */
  workspaceConversations(opts?: {
    before?: string;
    limit?: number;
    /** `only` is the recovery read: archived conversations, same grants. */
    archived?: "only";
    /** Narrow to one specialist; never widens the caller's reachable set. */
    agentId?: string;
    /** Case-insensitive substring over every renderable message in a conversation. */
    q?: string;
    /** Which conversation class to list. Omitted means ordinary chats. */
    kind?: ConversationKindFilter;
    /** Return an empty unchanged shape when this opaque scoped revision still matches. */
    ifRevision?: string;
  }): Promise<WorkspaceConversationsData>;
  users: {
    list(): Promise<UsersListData>;
    get(userId: string): Promise<UserDetailData>;
    /** Admin creates the account; the server emails the user a sign-in link. */
    create(payload: UserCreatePayload): Promise<UserCreateData>;
    remove(userId: string): Promise<{ ok: true }>;
    updateDisplayName(userId: string, displayName: string): Promise<UserDetailData>;
    setRole(userId: string, role: UserRole): Promise<UserSetRoleData>;
    grants: {
      list(userId: string): Promise<UserGrantsListData>;
      grant(
        userId: string,
        agentId: string,
        specialistRole: "owner" | "editor" | "user",
      ): Promise<{ ok: true }>;
      revoke(userId: string, agentId: string): Promise<{ ok: true }>;
    };
  };
  /** Programmatic access tokens. Admin-only end to end. */
  apiKeys: {
    list(): Promise<ApiKeysListData>;
    /** The returned `token` is shown once and never retrievable again. */
    create(payload: { name: string; userId: string }): Promise<ApiKeyCreateData>;
    revoke(keyId: string): Promise<{ ok: true }>;
  };
  /** The signed-in caller's own programmatic access tokens. */
  myApiKeys: {
    list(): Promise<ApiKeysListData>;
    /** The returned `token` is shown once and never retrievable again. */
    create(payload: { name: string }): Promise<ApiKeyCreateData>;
    revoke(keyId: string): Promise<{ ok: true }>;
  };
  approvalParcels: {
    list(
      status: "outstanding" | "decided",
      cursor?: string,
    ): Promise<z.infer<typeof ApprovalParcelListSchema>>;
    get(parcelId: string): Promise<z.infer<typeof ApprovalParcelViewSchema>>;
    decide(
      parcelId: string,
      verdict: "approved" | "rejected",
    ): Promise<z.infer<typeof ApprovalParcelDecisionSchema>>;
    fileText(parcelId: string, path: string): Promise<z.infer<typeof ApprovalFileTextSchema>>;
    fileUrl(parcelId: string, path: string): string;
    preferences(): Promise<z.infer<typeof ApprovalPreferencesSchema>>;
    setPreference(
      connectionId: string,
      enabled: boolean,
    ): Promise<z.infer<typeof ApprovalPreferencesSchema>>;
  };
  me: {
    /** Signed-in identity. 401 (thrown as `HubApiError`) means no session. */
    get(): Promise<MeData>;
    channels(): Promise<MeChannelsListData>;
    /**
     * Rename the signed-in caller. Self-scoped: the server takes the subject
     * from the session, so there is no user id to pass.
     */
    updateDisplayName(displayName: string): Promise<MeData>;
  };
  workflows: {
    list(opts?: { includeArchived?: boolean }): Promise<WorkflowListData>;
    get(workflowId: string): Promise<WorkflowGetData>;
    create(definition: WorkflowDefinitionWire): Promise<WorkflowCreateData>;
    update(
      workflowId: string,
      definition: WorkflowDefinitionWire,
      expectedVersion: number,
    ): Promise<WorkflowUpdateData>;
    setStatus(
      workflowId: string,
      status: WorkflowSetStatusPayload["status"],
    ): Promise<WorkflowSetStatusData>;
    items: {
      list(
        workflowId: string,
        opts?: { state?: string; includeClosed?: boolean },
      ): Promise<WorkflowItemsListData>;
      get(workflowId: string, itemId: string): Promise<WorkflowItemGetData>;
    };
  };
  /** Script automations. Access derives from the owning agent's grants. */
  automations: {
    /** Every automation on an agent the caller can view. */
    list(): Promise<AutomationListData>;
    get(agentId: string, automationId: string): Promise<AutomationDetailData>;
    update(
      agentId: string,
      automationId: string,
      patch: {
        status?: AutomationStatus;
        activeRevisionSeq?: number;
        confirmationMode?: AutomationConfirmationMode;
        schedule?: string | null;
      },
    ): Promise<AutomationSummary>;
    runs(agentId: string, automationId: string): Promise<AutomationRunsData>;
    run(
      agentId: string,
      automationId: string,
      body?: { args?: Record<string, unknown>; revisionSeq?: number },
    ): Promise<AutomationRunStartedData>;
    getRun(agentId: string, automationId: string, runId: string): Promise<AutomationRunDetailData>;
    cancelRun(
      agentId: string,
      automationId: string,
      runId: string,
    ): Promise<{ cancelled: boolean }>;
  };
  /** Operator dashboards. Access derives from the owning agent's grants. */
  dashboards: {
    list(): Promise<DashboardListData>;
    get(dashboardId: string): Promise<DashboardGetData>;
    updateVisibility(
      dashboardId: string,
      visibility: z.infer<typeof AssetVisibilitySchema>,
    ): Promise<z.infer<typeof AssetVisibilityResponseSchema>>;
    delete(dashboardId: string): Promise<DashboardDeleteData>;
    run(dashboardId: string): Promise<DashboardRunData>;
    runComponent(dashboardId: string, componentId: string): Promise<DashboardComponentRunData>;
    grants: {
      list(dashboardId: string): Promise<DashboardGrantsListData>;
      add(dashboardId: string, userId: string): Promise<DashboardGrantMutationData>;
      remove(dashboardId: string, userId: string): Promise<DashboardGrantMutationData>;
    };
  };
}

export class HubApiError extends Error {
  readonly status: number;
  readonly errorCode?: string;
  readonly requestId?: string;
  readonly details?: unknown;

  constructor(args: {
    status: number;
    error: string;
    errorCode?: string;
    requestId?: string;
    details?: unknown;
    cause?: unknown;
  }) {
    super(args.error, { cause: args.cause });
    this.name = "HubApiError";
    this.status = args.status;
    this.errorCode = args.errorCode;
    this.requestId = args.requestId;
    this.details = args.details;
  }
}

/**
 * The hub-chat CHANNEL session id inside a gateway session key
 * (`agent:<agentId>:channel:<uuid>`), or null for any other shape. The live
 * chat surface (composer + SSE) drives the channel id; a conversation row's
 * `sessionId` is the gateway record id. The two namespaces overlap, so a
 * caller sends whichever it holds together with the scope it belongs to —
 * `conversationAddress` below pairs them, and the record id cannot drive a
 * composer without creating a different conversation.
 */
export function channelSessionIdFromSessionKey(sessionKey: string): string | null {
  const tokens = sessionKey.split(":");
  if (tokens.length !== 4 || tokens[0] !== "agent" || tokens[2] !== "channel") return null;
  return tokens[3] || null;
}

/**
 * How to address one conversation on the chat routes: the id to send, and the
 * namespace it belongs to.
 *
 * The two must always travel together. A hub-chat conversation is addressed by
 * the channel id carried in its session key; everything else — and a hub-chat
 * row whose key cannot be parsed — is addressed by its record id. Deriving the
 * namespace from the branch that produced the id is the point: sending a record
 * UUID under `channel` resolves to nothing and answers 404.
 */
export function conversationAddress(conversation: {
  platform: string;
  sessionId: string;
  sessionKey: string;
}): { sessionId: string; address: "record" | "channel" } {
  const channelId =
    conversation.platform === HUB_CHAT_PLATFORM
      ? channelSessionIdFromSessionKey(conversation.sessionKey)
      : null;
  return channelId
    ? { sessionId: channelId, address: "channel" }
    : { sessionId: conversation.sessionId, address: "record" };
}

export const hubQueryKeys = {
  orgCurrent: () => ["hub", "org", "current"] as const,
  me: () => ["hub", "me"] as const,
  instanceBranding: () => ["hub", "instance", "branding"] as const,
  apiKeys: () => ["hub", "api-keys"] as const,
  myApiKeys: () => ["hub", "me", "api-keys"] as const,
  gatewayStatus: () => ["hub", "gateway", "status"] as const,
  slackChannelStatus: () => ["hub", "channels", "slack", "status"] as const,
  channelAdmin: () => ["hub", "channels", "admin"] as const,
  agents: () => ["hub", "agents"] as const,
  archivedAgents: () => ["hub", "agents", "archived"] as const,
  specialistTemplates: () => ["hub", "specialist-templates"] as const,
  applications: () => ["hub", "applications"] as const,
  internalApplication: (appId: string) => ["hub", "applications", "internal", appId] as const,
  agent: (agentId: string) => ["hub", "agents", agentId] as const,
  agentChatTarget: (agentId: string) => ["hub", "agents", agentId, "chat-target"] as const,
  templateDiff: (agentId: string) => ["hub", "agents", agentId, "template", "diff"] as const,
  agentTeams: () => ["hub", "settings", "agent-teams"] as const,
  credentialsRoot: () => ["hub", "credentials", "list"] as const,
  credentials: (agentId?: string) => ["hub", "credentials", "list", agentId ?? "all"] as const,
  connectorAdmin: () => ["hub", "connectors", "admin"] as const,
  connectorCatalog: () => ["hub", "connectors", "catalog"] as const,
  connectorList: (agentId: string) => ["hub", "connectors", "list", agentId] as const,
  connectorPermissions: (agentId: string) => ["hub", "connectors", "permissions", agentId] as const,
  permissions: (agentId: string) => ["hub", "agents", agentId, "permissions"] as const,
  // Prefix key for the entire per-agent memory subtree (topics, tags, entities,
  // entity detail all descend from it). Invalidating this one key refetches
  // every memory list at once after a mutation.
  memoryRoot: (agentId: string) => ["hub", "agents", agentId, "memory"] as const,
  memoryTopics: (agentId: string) => ["hub", "agents", agentId, "memory", "topics"] as const,
  memoryTags: (agentId: string) => ["hub", "agents", agentId, "memory", "tags"] as const,
  memoryEntryCount: (agentId: string) =>
    ["hub", "agents", agentId, "memory", "entry-count"] as const,
  // The filter (topic/tag/cursor) is part of the key so each filtered page is
  // cached independently and switching filters refetches rather than showing
  // a stale list.
  memoryEntities: (
    agentId: string,
    filter: { topic?: string; tag?: string; cursor?: string } = {},
  ) =>
    [
      "hub",
      "agents",
      agentId,
      "memory",
      "entities",
      { topic: filter.topic ?? null, tag: filter.tag ?? null, cursor: filter.cursor ?? null },
    ] as const,
  memoryEntity: (agentId: string, handle: string) =>
    ["hub", "agents", agentId, "memory", "entity", handle] as const,
  skills: (agentId: string) => ["hub", "agents", agentId, "skills"] as const,
  skillFiles: (agentId: string, skillId: string) =>
    ["hub", "agents", agentId, "skills", skillId, "files"] as const,
  skillFile: (agentId: string, skillId: string, path: string) =>
    ["hub", "agents", agentId, "skills", skillId, "files", path] as const,
  // Org-scoped skill catalog (the `/api/skills*` routes), distinct from the
  // agent-scoped `skills` enable/disable surface above.
  skillCatalog: () => ["hub", "skill-catalog"] as const,
  skillCatalogEntry: (skillId: string) => ["hub", "skill-catalog", skillId] as const,
  schedules: (agentId: string) => ["hub", "agents", agentId, "schedules"] as const,
  scheduleRuns: (agentId: string, scheduleId: string) =>
    ["hub", "agents", agentId, "schedules", scheduleId, "runs"] as const,
  scheduleRunTranscript: (agentId: string, scheduleId: string, runId: number) =>
    ["hub", "agents", agentId, "schedules", scheduleId, "runs", runId, "transcript"] as const,
  networkEgress: (agentId: string) => ["hub", "agents", agentId, "network-egress"] as const,
  agentEmail: (agentId: string) => ["hub", "agents", agentId, "email"] as const,
  agentEmailQuota: (agentId: string) => ["hub", "agents", agentId, "email", "quota"] as const,
  conversations: (agentId: string) => ["hub", "agents", agentId, "conversations"] as const,
  conversationHistory: (agentId: string, sessionId: string) =>
    ["hub", "agents", agentId, "conversations", sessionId] as const,
  conversationParticipants: (agentId: string, sessionId: string) =>
    ["hub", "agents", agentId, "conversations", sessionId, "participants"] as const,
  conversationAudience: (agentId: string) =>
    ["hub", "agents", agentId, "conversation-audience"] as const,
  /**
   * Every filter variant of the rail. Invalidating this reaches all of them,
   * which is what a send or an archive needs — the row moved lists, and which
   * filters happen to be mounted is not the writer's business.
   */
  workspaceConversations: () => ["hub", "workspace", "conversations"] as const,
  /**
   * One filter variant. Changing a filter is a different list rather than a
   * later page of the same one, so the keyset cursor resets by construction.
   */
  workspaceConversationsFiltered: (filters: {
    agentId?: string;
    q?: string;
    kind?: ConversationKindFilter;
  }) =>
    [
      "hub",
      "workspace",
      "conversations",
      filters.agentId ?? "all",
      filters.q ?? "",
      // Part of the key so switching the rail between chats and scheduled runs
      // starts a fresh list rather than appending a differently-classified page
      // onto the previous cursor.
      filters.kind ?? "chat",
    ] as const,
  /** The recovery list, keyed apart from the live rail it is recovering into. */
  archivedConversations: () => ["hub", "archived-conversations"] as const,
  users: () => ["hub", "users"] as const,
  user: (userId: string) => ["hub", "users", userId] as const,
  usageSummary: (range: UsageSummaryPayload) =>
    [
      "hub",
      "usage",
      "summary",
      range.window,
      range.window === "calendar-month" ? range.month : "current",
      range.agentIds ? [...range.agentIds].sort() : "all",
    ] as const,
  dashboardActivityToday: () => ["hub", "dashboard", "activity-today"] as const,
  decidedApprovals: () => ["hub", "approvals", "decided"] as const,
  userGrants: (userId: string) => ["hub", "users", userId, "grants"] as const,
  meChannels: () => ["hub", "me", "channels"] as const,
  workflowsRoot: () => ["hub", "workflows"] as const,
  workflows: (opts?: { includeArchived?: boolean }) =>
    ["hub", "workflows", { includeArchived: !!opts?.includeArchived }] as const,
  workflow: (workflowId: string) => ["hub", "workflows", workflowId] as const,
  workflowItems: (workflowId: string, opts?: { state?: string; includeClosed?: boolean }) =>
    [
      "hub",
      "workflows",
      workflowId,
      "items",
      { state: opts?.state ?? null, includeClosed: !!opts?.includeClosed },
    ] as const,
  workflowItem: (workflowId: string, itemId: string) =>
    ["hub", "workflows", workflowId, "items", itemId] as const,
  automations: () => ["hub", "automations"] as const,
  automation: (agentId: string, automationId: string) =>
    ["hub", "automations", agentId, automationId] as const,
  automationRuns: (agentId: string, automationId: string) =>
    ["hub", "automations", agentId, automationId, "runs"] as const,
  automationRun: (agentId: string, automationId: string, runId: string) =>
    ["hub", "automations", agentId, automationId, "runs", runId] as const,
  dashboards: () => ["hub", "dashboards"] as const,
  dashboard: (dashboardId: string) => ["hub", "dashboards", dashboardId] as const,
  dashboardGrants: (dashboardId: string) => ["hub", "dashboards", dashboardId, "grants"] as const,
};

/**
 * Per-request context the {@link performHubRequest} wrapper needs. The
 * `HubApiClient` closure supplies the operator's `baseUrl`/`headers`/401 hook;
 * standalone callers (e.g. the org-skills hooks) pass just `fetchImpl` and the
 * shared `onUnauthorized`.
 */
export interface HubRequestConfig {
  fetchImpl: typeof fetch;
  baseUrl?: string;
  headers?: HubApiClientOptions["headers"];
  onUnauthorized?: () => void;
}

/**
 * The one HTTP wrapper: fetch + `NETWORK_ERROR` wrap + 401 hook + JSON body
 * read + envelope unwrap + schema validation + `INVALID_RESPONSE`. `envelope`
 * selects the success-body shape — "data" unwraps the standard `{ ok, data }`
 * envelope; "flat" reads the identity routes' bare `{ ok, ... }` body
 * directly; "raw" takes the whole JSON body as-is (identity/branding routes
 * whose contract is a plain object keyed on the HTTP status alone).
 * Every caller names its response schema (pass `z.unknown()` to opt out), so an
 * endpoint is validated-or-a-compile-error, never a silent fail-open.
 */
export async function performHubRequest<T>(
  config: HubRequestConfig,
  path: string,
  init: RequestInit,
  schema: z.ZodType<T>,
  envelope: "data" | "flat" | "raw" = "data",
): Promise<T> {
  const url = toHubApiUrl(path, config.baseUrl);
  let response: Response;
  try {
    response = await config.fetchImpl(url, {
      credentials: "include",
      ...init,
      headers: await mergeHeaders(config.headers, init.headers),
    });
  } catch (err) {
    throw new HubApiError({
      status: 0,
      error: "Unable to reach Hub API",
      errorCode: "NETWORK_ERROR",
      cause: err,
    });
  }

  // A 401 on any `/api/*` call means the session is stale or absent.
  // Defer to the global handler before surfacing the error.
  if (response.status === 401) {
    config.onUnauthorized?.();
  }

  const body = await readJsonBody(response);
  if (!response.ok) {
    throw hubApiErrorFromResponse(response, body);
  }

  let data: unknown;
  if (envelope === "raw") {
    data = body;
  } else if (envelope === "flat") {
    // Identity routes (`/api/users/*`, `/api/me/channels`) return a flat
    // `{ ok, ... }` success body rather than `{ ok, data }` — part of their
    // public contract. Failures still use the canonical Hub error envelope.
    if (!isRecord(body) || body.ok !== true) {
      throw new HubApiError({
        status: response.status,
        error: "Hub API returned an invalid response",
        errorCode: "INVALID_RESPONSE",
        details: body,
      });
    }
    data = body;
  } else {
    const parsed = parseHttpEnvelope(body);
    if (!parsed) {
      throw new HubApiError({
        status: response.status,
        error: "Hub API returned an invalid response",
        errorCode: "INVALID_RESPONSE",
        details: body,
      });
    }
    if (!parsed.ok) {
      throw hubApiErrorFromEnvelope(response.status, parsed);
    }
    data = parsed.data;
  }

  const validated = schema.safeParse(data);
  if (!validated.success) {
    throw new HubApiError({
      status: response.status,
      error: "Hub API returned invalid data",
      errorCode: "INVALID_RESPONSE",
      details: { issues: validated.error.issues },
    });
  }
  return validated.data;
}

export function createHubApiClient(options: HubApiClientOptions = {}): HubApiClient {
  const config: HubRequestConfig = {
    // Bound deliberately. `performHubRequest` invokes this as
    // `config.fetchImpl(...)`, which makes `config` the receiver; native fetch
    // is a WebIDL operation on the global and throws "Illegal invocation" for
    // any other `this`. Storing a bare reference broke every request in the
    // browser while node/jsdom — which do not enforce the receiver — kept the
    // unit suite green.
    fetchImpl: options.fetch ?? globalThis.fetch.bind(globalThis),
    baseUrl: options.baseUrl,
    headers: options.headers,
    onUnauthorized: options.onUnauthorized,
  };
  const call = <T>(
    path: string,
    init: RequestInit,
    schema: z.ZodType<T>,
    envelope: "data" | "flat" | "raw" = "data",
  ): Promise<T> => performHubRequest(config, path, init, schema, envelope);

  return {
    request: <TData>(path: string, init: RequestInit = {}) =>
      call<TData>(path, init, z.unknown() as z.ZodType<TData>),
    instanceBranding: () =>
      call("/api/instance/branding", {}, InstanceBrandingDataResponseSchema, "raw"),
    org: {
      current: () => call("/api/org/current", {}, CurrentOrgDataResponseSchema),
      updateBranding: (payload) =>
        call("/api/org/branding", jsonInit("PATCH", payload), OrgBrandingDataResponseSchema),
      uploadLogo: (file) => {
        const form = new FormData();
        form.append("file", file);
        return call(
          "/api/org/branding/logo",
          { method: "POST", body: form },
          OrgBrandingDataResponseSchema,
        );
      },
      deleteLogo: () =>
        call("/api/org/branding/logo", { method: "DELETE" }, OrgBrandingDataResponseSchema),
      updateSecurity: (enabled) =>
        call(
          "/api/org/security",
          jsonInit("PATCH", { bypassConfirmationsEnabled: enabled }),
          z.object({ bypassConfirmationsEnabled: z.boolean() }).passthrough(),
        ),
    },
    gateway: {
      status: () => call("/api/gateway/status", {}, GatewayStatusDataResponseSchema),
      restart: () => call("/api/gateway/restart", { method: "POST" }, RestartDataSchema),
      eventsUrl: () => toHubApiUrl("/api/gateway/events", options.baseUrl),
    },
    channels: {
      list: () => call("/api/channels", {}, ChannelListDataResponseSchema, "flat"),
      googleChat: {
        install: (input) =>
          call(
            "/api/channels/google-chat/credential",
            jsonInit("PUT", input),
            GoogleChatChannelDataResponseSchema,
            "flat",
          ),
        setEnabled: (enabled) =>
          call(
            "/api/channels/google-chat",
            jsonInit("PATCH", { enabled }),
            GoogleChatChannelDataResponseSchema,
            "flat",
          ),
        remove: () =>
          call(
            "/api/channels/google-chat/credential",
            { method: "DELETE" },
            GoogleChatChannelDataResponseSchema,
            "flat",
          ),
      },
      slack: {
        status: () => call("/api/channels/slack/status", {}, SlackChannelStatusDataResponseSchema),
      },
    },
    agentTeams: {
      list: () => call("/api/settings/agent-teams", {}, AgentTeamsListDataSchema),
      create: (payload) =>
        call("/api/settings/agent-teams", jsonInit("POST", payload), AgentTeamsCreateDataSchema),
      update: (agentTeamId, payload) =>
        call(
          `/api/settings/agent-teams/${encodeSegment(agentTeamId)}`,
          jsonInit("PATCH", payload),
          AgentTeamsUpdateDataSchema,
        ),
      delete: (agentTeamId) =>
        call(
          `/api/settings/agent-teams/${encodeSegment(agentTeamId)}`,
          { method: "DELETE" },
          AgentTeamsDeleteDataSchema,
        ),
    },
    usage: {
      summary: (payload) =>
        call("/api/usage/summary", jsonInit("POST", payload), UsageSummaryDataSchema),
    },
    dashboard: {
      activityToday: () =>
        call("/api/dashboard/activity-today", {}, DashboardTodayActivityDataResponseSchema),
      pendingConfirmationsEventsUrl: () =>
        toHubApiUrl("/api/dashboard/pending-confirmations", options.baseUrl),
      decidedApprovals: (confirmationIds = []) =>
        call(
          "/api/approvals/reconcile",
          jsonInit("POST", { confirmationIds }),
          DecidedApprovalsDataResponseSchema,
        ),
      resolvePendingConfirmation: ({ agentId, sessionId, confirmationId, decision }) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/chat/sessions/${encodeSegment(sessionId)}/confirmations/${encodeSegment(confirmationId)}`,
          jsonInit("POST", { decision }),
          HubChatConfirmAckSchema,
        ),
    },
    applications: {
      list: () => call("/api/applications", {}, ApplicationsListResponseSchema),
      getInternal: (appId) =>
        call(
          `/api/applications/internal/${encodeSegment(appId)}`,
          {},
          InternalApplicationResponseSchema,
        ),
      createSession: (appId) =>
        call(
          `/api/applications/internal/${encodeSegment(appId)}/session`,
          { method: "POST" },
          ApplicationSessionTicketSchema,
          "raw",
        ),
      sessionSocketUrl: (socketPath, pageHref) =>
        applicationSocketUrl(toHubApiUrl(socketPath, options.baseUrl), pageHref),
      runAction: (appId, actionName, args) =>
        call(
          `/api/applications/internal/${encodeSegment(appId)}/actions/${encodeSegment(actionName)}`,
          jsonInit("POST", { args }),
          ApplicationActionResultSchema,
        ),
      updateVisibility: (appId, visibility) =>
        call(
          `/api/applications/internal/${encodeSegment(appId)}/visibility`,
          jsonInit("PATCH", { visibility }),
          AssetVisibilityResponseSchema,
        ),
      createExternal: (input) =>
        call("/api/applications/external", jsonInit("POST", input), ExternalApplicationSchema),
      updateExternal: (id, input) =>
        call(
          `/api/applications/external/${encodeSegment(id)}`,
          jsonInit("PATCH", input),
          ExternalApplicationSchema,
        ),
      deleteInternal: (appId) =>
        call(
          `/api/applications/internal/${encodeSegment(appId)}`,
          { method: "DELETE" },
          z.object({ deleted: z.boolean() }).passthrough(),
        ),
      deleteExternal: (id) =>
        call(
          `/api/applications/external/${encodeSegment(id)}`,
          { method: "DELETE" },
          z.object({ deleted: z.boolean() }).passthrough(),
        ),
    },
    agents: {
      list: () => call("/api/agents", {}, AgentListDataSchema),
      listArchived: () => call("/api/agents/archived", {}, AgentArchivedListDataSchema),
      templates: () => call("/api/specialist-templates", {}, SpecialistTemplatesDataSchema),
      get: (agentId) => call(`/api/agents/${encodeSegment(agentId)}`, {}, AgentGetDataSchema),
      chatTarget: (agentId) =>
        call(`/api/agents/${encodeSegment(agentId)}/chat-target`, {}, AgentChatTargetDataSchema),
      create: (payload) => call("/api/agents", jsonInit("POST", payload), AgentGetDataSchema),
      update: (agentId, payload) =>
        call(
          `/api/agents/${encodeSegment(agentId)}`,
          jsonInit("PATCH", payload),
          AgentUpdateDataSchema,
        ),
      delete: (agentId) =>
        call(`/api/agents/${encodeSegment(agentId)}`, { method: "DELETE" }, AgentDeleteDataSchema),
      archive: (agentId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/archive`,
          { method: "POST" },
          AgentArchiveDataSchema,
        ),
      unarchive: (agentId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/unarchive`,
          { method: "POST" },
          AgentUnarchiveDataSchema,
        ),
      uploadAvatar: (agentId, file) => {
        const form = new FormData();
        form.append("file", file);
        return call(
          `/api/agents/${encodeSegment(agentId)}/avatar`,
          {
            method: "POST",
            body: form,
          },
          AvatarUploadDataResponseSchema,
        );
      },
      deleteAvatar: (agentId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/avatar`,
          {
            method: "DELETE",
          },
          AvatarDeleteDataResponseSchema,
        ),
      templateDiff: (agentId) =>
        call(`/api/agents/${encodeSegment(agentId)}/template/diff`, {}, TemplateDiffDataSchema),
      templateApply: (agentId, payload) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/template/apply`,
          jsonInit("POST", payload),
          TemplateApplyDataSchema,
        ),
    },
    credentials: {
      list: (agentId) =>
        call(
          agentId ? `/api/credentials?agentId=${encodeURIComponent(agentId)}` : "/api/credentials",
          {},
          CredentialListDataSchema,
        ),
      set: (payload) =>
        call("/api/credentials", jsonInit("POST", payload), CredentialSetDataSchema),
      update: (namespace, key, payload) => {
        const { agentId, ...body } = payload;
        return call(
          credentialResourcePath(namespace, key, agentId),
          jsonInit("PATCH", body),
          CredentialUpdateDataSchema,
        );
      },
      delete: (namespace, key, agentId) =>
        call(
          credentialResourcePath(namespace, key, agentId),
          {
            method: "DELETE",
          },
          CredentialDeleteDataSchema,
        ),
    },
    mcp: {
      connectUrl: (params) => {
        const query = new URLSearchParams();
        query.set("serverId", params.serverId);
        query.set("agentId", params.agentId);
        query.set("profileName", params.profileName);
        if (params.additionalScopes && params.additionalScopes.length > 0) {
          query.set("additionalScopes", params.additionalScopes.join(" "));
        }
        return toHubApiUrl(`/api/mcp/oauth/connect?${query.toString()}`, options.baseUrl);
      },
    },
    connectors: {
      catalog: () => call("/api/connectors/catalog", {}, ConnectorCatalogDataResponseSchema),
      admin: {
        list: () => call("/api/connectors/admin", {}, ConnectorAdminListDataResponseSchema),
        setEnabled: (connectorType, enabled) =>
          call(
            `/api/connectors/admin/${encodeURIComponent(connectorType)}`,
            jsonInit("PATCH", { enabled }),
            ConnectorAdminRowDataResponseSchema,
          ),
        saveConfig: (connectorType, payload) =>
          call(
            `/api/connectors/admin/${encodeURIComponent(connectorType)}/config`,
            jsonInit("PUT", payload),
            ConnectorAdminRowDataResponseSchema,
          ),
        deleteConfig: (connectorType) =>
          call(
            `/api/connectors/admin/${encodeURIComponent(connectorType)}/config`,
            { method: "DELETE" },
            ConnectorAdminRowDataResponseSchema,
          ),
      },
      list: (agentId) =>
        call(
          `/api/connectors?agentId=${encodeURIComponent(agentId)}`,
          {},
          ConnectorListDataResponseSchema,
        ),
      connect: (payload) =>
        call(
          "/api/connectors/connect",
          jsonInit("POST", payload),
          ConnectorConnectDataResponseSchema,
        ),
      setCredentials: (payload) =>
        call(
          "/api/connectors/credentials",
          jsonInit("POST", payload),
          ConnectorCredentialsDataResponseSchema,
        ),
      discover: (payload) =>
        call(
          "/api/connectors/discover",
          jsonInit("POST", payload),
          ConnectorDiscoverDataResponseSchema,
        ),
      disconnect: (payload) =>
        call(
          "/api/connectors/disconnect",
          jsonInit("POST", payload),
          ConnectorVisibilityDataResponseSchema,
        ),
      reconnect: (payload) =>
        call(
          "/api/connectors/reconnect",
          jsonInit("POST", payload),
          ConnectorReconnectDataResponseSchema,
        ),
      rename: (payload) =>
        call(
          "/api/connectors/rename",
          jsonInit("POST", payload),
          ConnectorRenameDataResponseSchema,
        ),
      permissions: {
        get: (agentId) =>
          call(
            `/api/connectors/permissions?agentId=${encodeURIComponent(agentId)}`,
            {},
            ConnectorPermissionsDataResponseSchema,
          ),
        set: (payload) =>
          call(
            "/api/connectors/permissions",
            jsonInit("PUT", payload),
            ConnectorVisibilityDataResponseSchema,
          ),
      },
      completeOAuth: (payload, signal) =>
        call(
          CONNECTOR_OAUTH_COMPLETE_PATH,
          {
            ...jsonInit("POST", payload),
            signal,
          },
          OAuthCompleteDataResponseSchema,
        ),
    },
    permissions: {
      list: (agentId) =>
        call(`/api/agents/${encodeSegment(agentId)}/permissions`, {}, PermissionListDataSchema),
      set: (agentId, tool, payload) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/permissions/${encodeSegment(tool)}`,
          jsonInit("PATCH", payload),
          PermissionSetDataSchema,
        ),
    },
    memory: {
      list: (agentId, params) => {
        const q = new URLSearchParams();
        if (params?.topic) q.set("topic", params.topic);
        if (params?.tag) q.set("tag", params.tag);
        if (params?.cursor) q.set("cursor", params.cursor);
        if (params?.limit !== undefined) q.set("limit", String(params.limit));
        const qs = q.toString();
        return call(
          `/api/agents/${encodeSegment(agentId)}/memory${qs ? `?${qs}` : ""}`,
          {},
          MemoryListByFilterDataSchema,
        );
      },
      read: (agentId, handle) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/memory/${encodeSegment(handle)}`,
          {},
          MemoryReadByHandleDataSchema,
        ),
      write: (agentId, handle, input) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/memory/${encodeSegment(handle)}`,
          jsonInit("PUT", input),
          MemoryWriteByHandleDataSchema,
        ),
      delete: (agentId, handle) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/memory/${encodeSegment(handle)}`,
          { method: "DELETE" },
          MemoryDeleteByHandleDataSchema,
        ),
      downloadUrl: (agentId, handle) =>
        toHubApiUrl(
          `/api/agents/${encodeSegment(agentId)}/memory/${encodeSegment(handle)}/download`,
          options.baseUrl,
        ),
      topics: {
        list: (agentId) =>
          call(
            `/api/agents/${encodeSegment(agentId)}/memory-topics`,
            {},
            MemoryListTopicsDataSchema,
          ),
        save: (agentId, input) =>
          call(
            `/api/agents/${encodeSegment(agentId)}/memory-topics`,
            jsonInit("POST", input),
            MemorySaveTopicDataSchema,
          ),
        delete: (agentId, id) =>
          call(
            `/api/agents/${encodeSegment(agentId)}/memory-topics/${encodeSegment(id)}`,
            { method: "DELETE" },
            MemoryDeleteTopicDataSchema,
          ),
        rename: (agentId, id, newId) =>
          call(
            `/api/agents/${encodeSegment(agentId)}/memory-topics/${encodeSegment(id)}/rename`,
            jsonInit("PATCH", { newId }),
            MemoryRenameTopicDataSchema,
          ),
      },
      tags: {
        list: (agentId) =>
          call(`/api/agents/${encodeSegment(agentId)}/memory-tags`, {}, MemoryListTagsDataSchema),
        save: (agentId, input) =>
          call(
            `/api/agents/${encodeSegment(agentId)}/memory-tags`,
            jsonInit("POST", input),
            MemorySaveTagDataSchema,
          ),
        delete: (agentId, name) =>
          call(
            `/api/agents/${encodeSegment(agentId)}/memory-tags/${encodeSegment(name)}`,
            { method: "DELETE" },
            MemoryDeleteTagDataSchema,
          ),
        rename: (agentId, name, newName) =>
          call(
            `/api/agents/${encodeSegment(agentId)}/memory-tags/${encodeSegment(name)}/rename`,
            jsonInit("PATCH", { newName }),
            MemoryRenameTagDataSchema,
          ),
      },
    },
    workspace: {
      list: (agentId, path) =>
        call(
          path
            ? `/api/agents/${encodeSegment(agentId)}/workspace/${encodePath(path)}?mode=list`
            : `/api/agents/${encodeSegment(agentId)}/workspace`,
          {},
          WorkspaceListDataSchema,
        ),
      read: (agentId, path) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/workspace/${encodePath(path)}`,
          {},
          WorkspaceReadDataSchema,
        ),
      writeText: (agentId, path, content, contentType = "text/plain") =>
        call(
          `/api/agents/${encodeSegment(agentId)}/workspace/${encodePath(path)}`,
          jsonInit("POST", { content, contentType, encoding: "utf8", overwrite: true }),
          WorkspaceWriteDataSchema,
        ),
      upload: (agentId, folderPath, file, overwrite = true) => {
        const form = new FormData();
        form.set("file", file);
        form.set("overwrite", overwrite ? "true" : "false");
        if (!folderPath) form.set("root", "true");
        const targetPath = folderPath || file.name;
        return call(
          `/api/agents/${encodeSegment(agentId)}/workspace/${encodePath(targetPath)}`,
          { method: "POST", body: form },
          WorkspaceWriteDataSchema,
        );
      },
      delete: (agentId, path) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/workspace/${encodePath(path)}`,
          { method: "DELETE" },
          WorkspaceDeleteDataSchema,
        ),
      mkdir: (agentId, path) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/workspace/folders`,
          jsonInit("POST", { path }),
          WorkspaceMkdirDataSchema,
        ),
      downloadUrl: (agentId, path) =>
        toHubApiUrl(
          `/api/agents/${encodeSegment(agentId)}/workspace-actions/download?${pathQuery(path)}`,
          options.baseUrl,
        ),
    },
    skills: {
      list: (agentId) =>
        call(`/api/agents/${encodeSegment(agentId)}/skills`, {}, SkillListDataSchema),
      add: (agentId, skillId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/skills`,
          jsonInit("POST", { skillId }),
          SkillAddDataSchema,
        ),
      remove: (agentId, skillId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/skills/${encodeSegment(skillId)}`,
          { method: "DELETE" },
          SkillRemoveDataSchema,
        ),
      configure: (agentId, skillId, config) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/skills/${encodeSegment(skillId)}/config`,
          jsonInit("PATCH", { config }),
          SkillConfigureDataSchema,
        ),
      files: (agentId, skillId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/skills/${encodeSegment(skillId)}/files`,
          {},
          SkillFilesListDataSchema,
        ),
      readFile: (agentId, skillId, path) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/skills/${encodeSegment(skillId)}/files/${encodePath(path)}`,
          {},
          SkillFilesReadDataSchema,
        ),
      upload: (agentId, form) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/skills/upload`,
          {
            method: "POST",
            body: form,
          },
          SkillUploadDataSchema,
        ),
      uploadFile: (agentId, skillId, path, file, overwrite = true) => {
        const form = new FormData();
        form.set("file", file);
        form.set("overwrite", overwrite ? "true" : "false");
        form.set("path", path);
        return call(
          `/api/agents/${encodeSegment(agentId)}/skills/${encodeSegment(skillId)}/files`,
          { method: "POST", body: form },
          SkillFilesWriteDataSchema,
        );
      },
      downloadFileUrl: (agentId, skillId, path) =>
        toHubApiUrl(
          `/api/agents/${encodeSegment(agentId)}/skills/${encodeSegment(skillId)}/file-actions/download?${pathQuery(path)}`,
          options.baseUrl,
        ),
    },
    schedules: {
      list: (agentId) =>
        call(`/api/agents/${encodeSegment(agentId)}/schedules`, {}, ScheduleListDataSchema),
      runs: (agentId, scheduleId, limit) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/schedules/${encodeSegment(scheduleId)}/runs${
            limit ? `?limit=${limit}` : ""
          }`,
          {},
          ScheduleRunsDataSchema,
        ),
      transcript: (agentId, scheduleId, runId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/schedules/${encodeSegment(scheduleId)}/runs/${runId}/transcript`,
          {},
          ScheduleRunTranscriptDataSchema,
        ),
      update: (agentId, scheduleId, patch) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/schedules/${encodeSegment(scheduleId)}`,
          jsonInit("PATCH", { patch }),
          ScheduleUpdateDataSchema,
        ),
      action: (agentId, scheduleId, action) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/schedules/${encodeSegment(scheduleId)}/actions`,
          jsonInit("POST", { action }),
          ScheduleActionDataSchema,
        ),
    },
    browser: {
      get: (agentId) =>
        call(`/api/agents/${encodeSegment(agentId)}/browser`, {}, BrowserStatusSchema),
      configure: (agentId, enabled) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/browser`,
          jsonInit("PATCH", { enabled }),
          BrowserStatusSchema,
        ),
      forget: (agentId, origin) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/browser/forget`,
          jsonInit("POST", { origin }),
          BrowserStatusSchema,
        ),
      reset: (agentId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/browser/reset`,
          jsonInit("POST", {}),
          BrowserStatusSchema,
        ),
      ticket: (agentId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/browser/socket-ticket`,
          jsonInit("POST", {}),
          z.object({ ticket: z.string() }),
        ),
      handoff: (agentId, input) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/browser/handoff`,
          jsonInit("POST", input),
          z.object({ outcome: z.string() }),
        ),
    },
    email: {
      get: (agentId) =>
        call(`/api/agents/${encodeSegment(agentId)}/email`, {}, AgentEmailSettingsSchema),
      update: (agentId, patch) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/email`,
          jsonInit("PUT", patch),
          AgentEmailSettingsSchema,
        ),
      quota: (agentId) =>
        call(`/api/agents/${encodeSegment(agentId)}/email/quota`, {}, AgentEmailQuotaSchema),
    },
    networkEgress: {
      get: (agentId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/network-egress`,
          {},
          NetworkEgressGetDataSchema,
        ),
      update: (agentId, config) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/network-egress`,
          jsonInit("PUT", config),
          NetworkEgressUpdateDataSchema,
        ),
    },
    conversations: {
      create: (agentId, sessionId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/chat/sessions`,
          jsonInit("POST", { sessionId }),
          ConversationCreateDataSchema,
        ),
      resolve: (agentId, sessionId, address) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/chat/sessions/${encodeSegment(sessionId)}${address ? `?address=${address}` : ""}`,
          {},
          ConversationResolutionDataSchema,
        ),
      list: (agentId, opts) => {
        const query = new URLSearchParams();
        if (opts?.archived === "only") query.set("archived", "only");
        if (opts?.kind) query.set("kind", opts.kind);
        const suffix = query.size > 0 ? `?${query.toString()}` : "";
        return call(
          `/api/agents/${encodeSegment(agentId)}/conversations${suffix}`,
          {},
          SessionListDataSchema,
        );
      },
      history: (agentId, sessionId, opts) => {
        const query = new URLSearchParams();
        query.set("address", opts?.address ?? "record");
        if (opts?.limit !== undefined) query.set("limit", String(opts.limit));
        if (opts?.before !== undefined) query.set("before", String(opts.before));
        const suffix = query.size > 0 ? `?${query.toString()}` : "";
        return call(
          `/api/agents/${encodeSegment(agentId)}/conversations/${encodeSegment(sessionId)}${suffix}`,
          {},
          ConversationPageSchema,
        );
      },
      send: (agentId, input) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/chat/messages`,
          jsonInit("POST", {
            sessionId: input.sessionId,
            text: input.text,
            clientMessageId: input.clientMessageId,
            ...(input.attachments && input.attachments.length > 0
              ? { attachments: input.attachments }
              : {}),
          }),
          HubChatSendResponseSchema,
          "raw",
        ),
      setArchived: (agentId, sessionId, input) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/chat/sessions/${encodeSegment(sessionId)}/${
            input.archived ? "archive" : "unarchive"
          }?address=${input.address ?? "channel"}`,
          jsonInit("POST", {}),
          ConversationArchiveDataSchema,
        ),
      setConfirmationMode: (agentId, sessionId, confirmationMode) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/conversations/${encodeSegment(sessionId)}/confirmation-mode`,
          jsonInit("PATCH", { confirmationMode }),
          z.object({ confirmationMode: z.enum(["manual", "bypass"]) }).passthrough(),
        ),
      participants: {
        list: (agentId, sessionId) =>
          call(
            `/api/agents/${encodeSegment(agentId)}/chat/sessions/${encodeSegment(sessionId)}/participants`,
            {},
            ConversationParticipantsDataSchema,
          ),
        invite: (agentId, sessionId, userId) =>
          call(
            `/api/agents/${encodeSegment(agentId)}/chat/sessions/${encodeSegment(sessionId)}/participants`,
            jsonInit("POST", { userId }),
            ConversationParticipantsDataSchema,
          ),
        leave: (agentId, sessionId) =>
          call(
            `/api/agents/${encodeSegment(agentId)}/chat/sessions/${encodeSegment(sessionId)}/participants/me`,
            { method: "DELETE" },
            ConversationLeaveDataSchema,
          ),
      },
      audience: (agentId) =>
        call(`/api/agents/${encodeSegment(agentId)}/audience`, {}, ConversationAudienceDataSchema),
    },
    workspaceConversations: (opts) => {
      const query = new URLSearchParams();
      if (opts?.before !== undefined) query.set("before", opts.before);
      if (opts?.limit !== undefined) query.set("limit", String(opts.limit));
      if (opts?.archived !== undefined) query.set("archived", opts.archived);
      if (opts?.agentId) query.set("agentId", opts.agentId);
      if (opts?.q) query.set("q", opts.q);
      if (opts?.kind) query.set("kind", opts.kind);
      if (opts?.ifRevision) query.set("ifRevision", opts.ifRevision);
      const suffix = query.size > 0 ? `?${query.toString()}` : "";
      return call(`/api/workspace/conversations${suffix}`, {}, WorkspaceConversationsDataSchema);
    },
    users: {
      list: () => call("/api/users", {}, UsersListResponseSchema, "flat"),
      get: (userId) =>
        call(`/api/users/${encodeSegment(userId)}`, {}, UserDetailResponseSchema, "flat"),
      create: (payload) =>
        call("/api/users", jsonInit("POST", payload), UserCreateResponseSchema, "flat"),
      remove: (userId) =>
        call(
          `/api/users/${encodeSegment(userId)}`,
          { method: "DELETE" },
          UserOkResponseSchema,
          "flat",
        ),
      updateDisplayName: (userId, displayName) =>
        call(
          `/api/users/${encodeSegment(userId)}`,
          jsonInit("PATCH", { displayName }),
          UserDetailResponseSchema,
          "flat",
        ),
      setRole: (userId, role) =>
        call(
          `/api/users/${encodeSegment(userId)}/role`,
          jsonInit("PATCH", { role }),
          UserSetRoleResponseSchema,
          "flat",
        ),
      grants: {
        list: (userId) =>
          call(
            `/api/users/${encodeSegment(userId)}/grants`,
            {},
            UserGrantsListResponseSchema,
            "flat",
          ),
        grant: (userId, agentId, specialistRole) =>
          call(
            `/api/users/${encodeSegment(userId)}/grants`,
            jsonInit("POST", { agentId, specialistRole }),
            UserOkResponseSchema,
            "flat",
          ),
        revoke: (userId, agentId) =>
          call(
            `/api/users/${encodeSegment(userId)}/grants`,
            {
              method: "DELETE",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({ agentId }),
            },
            UserOkResponseSchema,
            "flat",
          ),
      },
    },
    apiKeys: {
      list: () => call("/api/api-keys", {}, ApiKeysListResponseSchema, "raw"),
      create: (payload) =>
        call("/api/api-keys", jsonInit("POST", payload), ApiKeyCreateResponseSchema, "raw"),
      revoke: (keyId) =>
        call(
          `/api/api-keys/${encodeSegment(keyId)}`,
          { method: "DELETE" },
          ApiKeyRevokeResponseSchema,
          "raw",
        ),
    },
    myApiKeys: {
      list: () => call("/api/me/api-keys", {}, ApiKeysListResponseSchema, "raw"),
      create: (payload) =>
        call("/api/me/api-keys", jsonInit("POST", payload), ApiKeyCreateResponseSchema, "raw"),
      revoke: (keyId) =>
        call(
          `/api/me/api-keys/${encodeSegment(keyId)}`,
          { method: "DELETE" },
          ApiKeyRevokeResponseSchema,
          "raw",
        ),
    },
    approvalParcels: {
      list: (status, cursor) =>
        call(
          `/api/approvals/parcels?${new URLSearchParams({ status, ...(cursor ? { cursor } : {}) })}`,
          {},
          ApprovalParcelListSchema,
        ),
      get: (id) =>
        call(`/api/approvals/parcels/${encodeSegment(id)}`, {}, ApprovalParcelViewSchema),
      decide: (id, verdict) =>
        call(
          `/api/approvals/parcels/${encodeSegment(id)}/decision`,
          jsonInit("POST", { verdict }),
          ApprovalParcelDecisionSchema,
        ),
      fileText: (id, path) =>
        call(
          `/api/approvals/parcels/${encodeSegment(id)}/file?${new URLSearchParams({ path, text: "true" })}`,
          {},
          ApprovalFileTextSchema,
        ),
      fileUrl: (id, path) =>
        toHubApiUrl(
          `/api/approvals/parcels/${encodeSegment(id)}/file?${new URLSearchParams({ path })}`,
          options.baseUrl,
        ),
      preferences: () => call("/api/me/approval-notifications", {}, ApprovalPreferencesSchema),
      setPreference: (id, enabled) =>
        call(
          `/api/me/approval-notifications/${encodeSegment(id)}`,
          jsonInit("PATCH", { enabled }),
          ApprovalPreferencesSchema,
        ),
    },
    me: {
      get: () => call("/api/me", {}, MeDataResponseSchema, "raw"),
      channels: () => call("/api/me/channels", {}, MeChannelsListResponseSchema, "flat"),
      updateDisplayName: (displayName) =>
        call("/api/me", jsonInit("PATCH", { displayName }), MeDataResponseSchema, "raw"),
    },
    workflows: {
      list: (opts) => {
        const q = opts?.includeArchived ? "?includeArchived=true" : "";
        return call(`/api/workflows${q}`, {}, WorkflowListDataSchema);
      },
      get: (workflowId) =>
        call(`/api/workflows/${encodeSegment(workflowId)}`, {}, WorkflowGetDataSchema),
      create: (definition) =>
        call(
          "/api/workflows",
          jsonInit("POST", { definition } satisfies WorkflowCreatePayload),
          WorkflowCreateDataSchema,
        ),
      update: (workflowId, definition, expectedVersion) =>
        call(
          `/api/workflows/${encodeSegment(workflowId)}`,
          {
            method: "PUT",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ definition, expectedVersion }),
          },
          WorkflowUpdateDataSchema,
        ),
      setStatus: (workflowId, status) =>
        call(
          `/api/workflows/${encodeSegment(workflowId)}/status`,
          jsonInit("PATCH", { status }),
          WorkflowSetStatusDataSchema,
        ),
      items: {
        list: (workflowId, opts) => {
          const params = new URLSearchParams();
          if (opts?.state) params.set("state", opts.state);
          if (opts?.includeClosed) params.set("includeClosed", "true");
          const suffix = params.size > 0 ? `?${params.toString()}` : "";
          return call(
            `/api/workflows/${encodeSegment(workflowId)}/items${suffix}`,
            {},
            WorkflowItemsListDataSchema,
          );
        },
        get: (workflowId, itemId) =>
          call(
            `/api/workflows/${encodeSegment(workflowId)}/items/${encodeSegment(itemId)}`,
            {},
            WorkflowItemGetDataSchema,
          ),
      },
    },
    automations: {
      list: () => call("/api/automations", {}, AutomationListDataSchema),
      get: (agentId, automationId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/automations/${encodeSegment(automationId)}`,
          {},
          AutomationDetailDataSchema,
        ),
      update: (agentId, automationId, patch) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/automations/${encodeSegment(automationId)}`,
          jsonInit("PATCH", patch),
          AutomationUpdateDataSchema,
        ),
      runs: (agentId, automationId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/automations/${encodeSegment(automationId)}/runs`,
          {},
          AutomationRunsDataSchema,
        ),
      run: (agentId, automationId, body) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/automations/${encodeSegment(automationId)}/runs`,
          jsonInit("POST", body ?? {}),
          AutomationRunStartedDataSchema,
        ),
      getRun: (agentId, automationId, runId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/automations/${encodeSegment(automationId)}/runs/${encodeSegment(runId)}`,
          {},
          AutomationRunDetailDataSchema,
        ),
      cancelRun: (agentId, automationId, runId) =>
        call(
          `/api/agents/${encodeSegment(agentId)}/automations/${encodeSegment(automationId)}/runs/${encodeSegment(runId)}/cancel`,
          jsonInit("POST", {}),
          AutomationCancelDataSchema,
        ),
    },
    dashboards: {
      list: () => call("/api/dashboards", {}, DashboardListResponseSchema, "flat"),
      get: (dashboardId) =>
        call(
          `/api/dashboards/${encodeSegment(dashboardId)}`,
          {},
          DashboardGetResponseSchema,
          "flat",
        ),
      updateVisibility: (dashboardId, visibility) =>
        call(
          `/api/dashboards/${encodeSegment(dashboardId)}/visibility`,
          jsonInit("PATCH", { visibility }),
          AssetVisibilityResponseSchema,
          "flat",
        ),
      delete: (dashboardId) =>
        call(
          `/api/dashboards/${encodeSegment(dashboardId)}`,
          { method: "DELETE" },
          DashboardDeleteResponseSchema,
          "flat",
        ),
      run: (dashboardId) =>
        call(
          `/api/dashboards/${encodeSegment(dashboardId)}/run`,
          jsonInit("POST", {}),
          DashboardRunResponseSchema,
          "flat",
        ),
      runComponent: (dashboardId, componentId) =>
        call(
          `/api/dashboards/${encodeSegment(dashboardId)}/components/${encodeSegment(componentId)}/run`,
          jsonInit("POST", {}),
          DashboardComponentRunResponseSchema,
          "flat",
        ),
      grants: {
        list: (dashboardId) =>
          call(
            `/api/dashboards/${encodeSegment(dashboardId)}/grants`,
            {},
            DashboardGrantsListResponseSchema,
            "flat",
          ),
        add: (dashboardId, userId) =>
          call(
            `/api/dashboards/${encodeSegment(dashboardId)}/grants`,
            jsonInit("POST", { userId }),
            DashboardGrantMutationResponseSchema,
            "flat",
          ),
        remove: (dashboardId, userId) =>
          call(
            `/api/dashboards/${encodeSegment(dashboardId)}/grants/${encodeSegment(userId)}`,
            { method: "DELETE" },
            DashboardGrantMutationResponseSchema,
            "flat",
          ),
      },
    },
  };
}

export function isGatewayOfflineError(error: unknown): boolean {
  return error instanceof HubApiError && error.errorCode === "GATEWAY_OFFLINE";
}

function jsonInit(method: "POST" | "PATCH" | "PUT", payload: unknown): RequestInit {
  return {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  };
}

function parseHttpEnvelope(value: unknown): HttpEnvelope | undefined {
  if (!isRecord(value) || typeof value.ok !== "boolean") {
    return undefined;
  }
  if (value.ok === true) {
    return "data" in value ? { ok: true, data: value.data } : undefined;
  }
  if (
    typeof value.error !== "string" ||
    value.error.length === 0 ||
    typeof value.errorCode !== "string" ||
    value.errorCode.length === 0
  ) {
    return undefined;
  }
  return {
    ok: false,
    error: value.error,
    errorCode: value.errorCode,
    ...(typeof value.requestId === "string" ? { requestId: value.requestId } : {}),
    ...("details" in value ? { details: value.details } : {}),
  };
}

function hubApiErrorFromResponse(response: Response, body: unknown): HubApiError {
  const parsed = parseHttpEnvelope(body);
  if (parsed && !parsed.ok) return hubApiErrorFromEnvelope(response.status, parsed);
  return new HubApiError({
    status: response.status,
    error: response.statusText || "Hub API returned an invalid error response",
    errorCode: "HTTP_ERROR",
    details: body,
  });
}

function hubApiErrorFromEnvelope(status: number, envelope: HttpErrorEnvelope): HubApiError {
  return new HubApiError({
    status,
    error: envelope.error,
    errorCode: envelope.errorCode,
    requestId: envelope.requestId,
    details: envelope.details,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

async function mergeHeaders(
  baseHeaders: HubApiClientOptions["headers"],
  initHeaders: HeadersInit | undefined,
): Promise<Headers> {
  const headers = new Headers(
    typeof baseHeaders === "function" ? await baseHeaders() : baseHeaders,
  );
  headers.set("accept", headers.get("accept") ?? "application/json");
  for (const [key, value] of new Headers(initHeaders)) {
    headers.set(key, value);
  }
  return headers;
}

async function readJsonBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    if (response.ok) return undefined;
    return { ok: false, error: response.statusText || "Hub API request failed" };
  }
  try {
    return await response.json();
  } catch (err) {
    return { ok: false, error: "Hub API returned invalid JSON", details: { cause: String(err) } };
  }
}

/**
 * Resolve a Hub `/api/*` path against an optional base URL. Exported so the
 * boot-time `auth-config` fetch, which runs before `<HubApiProvider>` mounts
 * and cannot use the React-Query client, resolves URLs identically to the
 * client — including `normalizeBaseUrl`.
 */
export function toHubApiUrl(path: string, baseUrl?: string): string {
  if (!path.startsWith("/api/")) {
    throw new Error("Hub UI client can only call Hub /api routes");
  }
  if (baseUrl) {
    return new URL(path, normalizeBaseUrl(baseUrl)).toString();
  }
  return path;
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
}

function encodeSegment(value: string): string {
  return encodeURIComponent(value);
}

function credentialResourcePath(namespace: string, key: string, agentId?: string): string {
  const base = `/api/credentials/${encodeSegment(namespace)}/${encodeSegment(key)}`;
  return agentId ? `${base}?agentId=${encodeURIComponent(agentId)}` : base;
}

function encodePath(path: string): string {
  return path
    .split("/")
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

function pathQuery(path: string): string {
  return `path=${encodeURIComponent(path)}`;
}
